import type { Product, ProductCategory } from "../types";
import { odooAssetUrl, odooDirectUrl } from "../odoo-asset";

// Product image assets (Locally hosted authentic VARS photography & 3D packaging)
import reviveDecapImg from "@/assets/products/optimized/revive-artemia-decap-1024.webp";
import reviveDecapThumb from "@/assets/products/thumbnails/revive-artemia-decap_4x3_lg.webp";
import reviveDecapOpenImg from "@/assets/products/optimized/revive-artemia-decap-open-1024.webp";
import reviveDecapBeakerMacro from "@/assets/products/optimized/revive-decap-beaker-macro-1024.webp";
import reviveDecapPouringStream from "@/assets/products/optimized/revive-decap-pouring-stream-1024.webp";
import reviveDecapBeakerFront from "@/assets/products/optimized/revive-decap-beaker-front-1024.webp";
import reviveDecapPouringWide from "@/assets/products/optimized/revive-decap-pouring-wide-1024.webp";

import reviveCystsImg from "@/assets/products/optimized/revive-artemia-cysts-1024.webp";
import reviveCystsThumb from "@/assets/products/thumbnails/revive-artemia-cysts_4x3_lg.webp";
import reviveCystsThumbSm from "@/assets/products/thumbnails/revive-artemia-cysts_4x3_sm.webp";
import reviveCystsBackImg from "@/assets/products/optimized/revive-artemia-cysts-back-1024.webp";
import reviveCysts2kgImg from "@/assets/products/optimized/revive-artemia-cysts-2kg-1024.webp";
import reviveCystsOpenImg from "@/assets/products/optimized/revive-artemia-cysts-open-512.webp";

import sv12ProductImg from "@/assets/products/optimized/super-fresh-chlorella-sv12-1024.webp";
import sv12ProductThumb from "@/assets/products/thumbnails/super-fresh-chlorella-sv12_4x3_lg.webp";
import sv12HighResImg from "@/assets/products/optimized/sv12-high-res-1024.webp";
import sv12PackImg from "@/assets/products/optimized/sv12-pack-1024.webp";
// Masters imported directly: sv12-box.jpg sits under the optimizer's 120 KB
// threshold and sv12-image-edited.webp is a webp master the script skips.
import sv12BoxImg from "@/assets/products/sv12-box.jpg";
import sv12BagImg from "@/assets/products/sv12-bag.jpg";
import sv12MacroImg from "@/assets/products/sv12-image-edited.webp";

import emeraldChlorellaImg from "@/assets/products/optimized/emerald-chlorella-powder-1024.webp";
import emeraldChlorellaThumb from "@/assets/products/thumbnails/emerald-chlorella-powder_4x3_lg.webp";
import emeraldChlorellaOpenedImg from "@/assets/products/optimized/emerald-chlorella-opened-1024.webp";

import salmonEggsScaleImg from "@/assets/products/atlantic-salmon-eggs-scale.jpg";
// Ova photography. Atlantic and Coho previously shared one Odoo image, so the
// two products were visually indistinguishable in the catalogue.
import ovaInHandImg from "@/assets/products/optimized/salmon-eyed-ova-in-hand-1024.webp";
import ovaMacroImg from "@/assets/products/optimized/salmon-eyed-ova-macro-1024.webp";
import ovaMacroThumbSm from "@/assets/products/thumbnails/salmon-eyed-ova-macro_4x3_sm.webp";
import ovaMacroThumbLg from "@/assets/products/thumbnails/salmon-eyed-ova-macro_4x3_lg.webp";
import ovaDetailImg from "@/assets/products/optimized/salmon-eyed-ova-detail-512.webp";
import ovaEarlyStageImg from "@/assets/products/optimized/salmon-ova-early-stage-macro-1024.webp";
import ovaIncubationTanksImg from "@/assets/products/optimized/salmon-ova-incubation-tanks-1024.webp";
import salmonEggsThermoImg from "@/assets/products/salmon-eggs-thermo.jpg";
import salmonTraysImg from "@/assets/products/optimized/salmon-incubation-trays-512.webp";

import fishMealImg from "@/assets/products/optimized/premium-fish-meal-1024.webp";
import fishMealThumbSm from "@/assets/products/thumbnails/premium-fish-meal_4x3_sm.webp";
import fishMealThumbLg from "@/assets/products/thumbnails/premium-fish-meal_4x3_lg.webp";
import wheatGlutenImg from "@/assets/products/wheat-gluten-container.jpg";
import enzyfishCoralImg from "@/assets/products/optimized/enzyfish-coral-powder-1024.webp";
import enzyfishCoralThumb from "@/assets/products/thumbnails/enzyfish-coral-powder_4x3_lg.webp";
import enzyfishSilverImg from "@/assets/products/optimized/enzyfish-silver-powder-1024.webp";
import enzyfishSilverThumb from "@/assets/products/thumbnails/enzyfish-silver-powder_4x3_lg.webp";
import enzyfishGoldImg from "@/assets/products/optimized/enzyfish-gold-powder-1024.webp";
import enzyfishGoldThumb from "@/assets/products/thumbnails/enzyfish-gold-powder_4x3_lg.webp";
import enzyfishLabSampleImg from "@/assets/products/optimized/enzyfish-lab-sample-1024.webp";
import enzyfishPowderTrioImg from "@/assets/products/optimized/enzyfish-powder-trio-1024.webp";

import seaBassImgFile from "@/assets/products/optimized/mediterranean-seabass-512.webp";
import seaBreamImgFile from "@/assets/products/optimized/mediterranean-seabream-512.webp";
import seaBreamThumbSm from "@/assets/products/thumbnails/mediterranean-seabream_4x3_sm.webp";
import seaBreamThumbLg from "@/assets/products/thumbnails/mediterranean-seabream_4x3_lg.webp";
import oliveFlounderImg from "@/assets/products/optimized/korean-olive-flounder-1024.webp";
import oliveFlounderLiveBasketImg from "@/assets/products/optimized/olive-flounder-live-basket-1024.webp";
import oliveFlounderFilletSpecsImg from "@/assets/products/optimized/olive-flounder-fillet-specs-1024.webp";
import oliveFlounderFilletPackImg from "@/assets/products/optimized/olive-flounder-fillet-vacuum-pack-1024.webp";
import oliveFlounderEpsPackImg from "@/assets/products/optimized/olive-flounder-vacuum-pack-eps-1024.webp";
import amberjackImg from "@/assets/products/greater-amberjack.jpg";
import shrimpImg from "@/assets/products/optimized/shrimp-1024.webp";
import shrimpFrozenBoxImg from "@/assets/products/shrimp-frozen-box-01.jpg";
import shrimpFrozenBlockImg from "@/assets/products/shrimp-frozen-block-02.jpg";
import shrimpFrozenCartonsImg from "@/assets/products/optimized/shrimp-frozen-cartons-03-512.webp";
import oysterImg from "@/assets/products/oyster.webp";

const reviveDecap = {
  url: reviveDecapImg,
  thumbnail: reviveDecapThumb,
  gallery: [
    reviveDecapOpenImg,
    reviveDecapPouringStream,
    reviveDecapBeakerMacro,
    reviveDecapBeakerFront,
    reviveDecapPouringWide,
  ],
};
const reviveCysts = {
  url: reviveCystsImg,
  thumbnail: reviveCystsThumb,
  gallery: [reviveCystsBackImg, reviveCysts2kgImg, reviveCystsOpenImg],
};
const sv12 = {
  url: sv12ProductImg,
  thumbnail: sv12ProductThumb,
  gallery: [sv12HighResImg, sv12PackImg, sv12BoxImg, sv12BagImg, sv12MacroImg],
};
const emeraldChlorella = {
  url: emeraldChlorellaImg,
  thumbnail: emeraldChlorellaThumb,
  gallery: [emeraldChlorellaOpenedImg],
};

// Salmon eggs requested default image URL
const salmonEggs = {
  url: odooAssetUrl(
    "/web/image/product.image/8/image_1920/Blog%20Post%20%27Fertilized%20Salmon%20Eggs_%20Where%20Every%20Salmon%20Farming%20Journey%20Begins%27%20cover%20image.webp?unique=ea15b07",
  ),
  gallery: [salmonEggsScaleImg, salmonEggsThermoImg, salmonTraysImg],
};

/**
 * Atlantic and Coho image sets.
 *
 * `url` stays the Odoo-hosted image both products have always used: it came
 * from the retired site, it is indexed, and Google Images ranks a picture
 * against the page it appears on. Replacing it as the primary would have
 * discarded that. The new ova photography is appended to the galleries instead,
 * so the pages gain images without losing any.
 *
 * The two galleries diverge after the shared three so the products are not
 * documented identically, which they were before.
 */
const SALMON_BASE_GALLERY = [salmonEggsScaleImg, salmonEggsThermoImg, salmonTraysImg];

const atlanticOva = {
  url: salmonEggs.url,
  gallery: [...SALMON_BASE_GALLERY, ovaMacroImg, ovaEarlyStageImg, ovaIncubationTanksImg],
};

const cohoOva = {
  url: salmonEggs.url,
  gallery: [...SALMON_BASE_GALLERY, ovaInHandImg, ovaDetailImg, ovaIncubationTanksImg],
};

// Feed Additives requested default image URLs
const fishMeal = {
  url: odooAssetUrl("/web/image/product.product/63/image_1024/Fish%20Meal?unique=23330ef"),
  gallery: [fishMealImg],
};

const wheatGluten = {
  url: odooAssetUrl(
    "/web/image/product.product/47/image_1024/Vital%20Wheat%20Gluten?unique=563ed38",
  ),
  gallery: [wheatGlutenImg],
};

const enzyfishCoral = {
  url: enzyfishCoralImg,
  thumbnail: enzyfishCoralThumb,
  gallery: [enzyfishPowderTrioImg, enzyfishLabSampleImg],
};

const enzyfishSilver = {
  url: enzyfishSilverImg,
  thumbnail: enzyfishSilverThumb,
  gallery: [enzyfishPowderTrioImg, enzyfishLabSampleImg],
};

const enzyfishGold = {
  url: enzyfishGoldImg,
  thumbnail: enzyfishGoldThumb,
  gallery: [enzyfishPowderTrioImg, enzyfishLabSampleImg],
};

// Seafood requested default image URLs from varsco.com/products/seafood
const seaBassImg = {
  url: odooAssetUrl("/web/image/9727-c596f7d9/levrek4_edited.webp"),
  gallery: [seaBassImgFile],
};

const seaBream = {
  url: odooAssetUrl(
    "/web/image/8924-1adb113f/customized_-c3-87-c3-bcpura_edited_e2c17a7c-48c8-4d84-b89a-0a1e054ae7ed.webp",
  ),
  gallery: [seaBreamImgFile],
};

const oliveFlounder = {
  url: odooAssetUrl("/web/image/9726-522f2377/olive-flounder.webp"),
  gallery: [
    oliveFlounderImg,
    oliveFlounderLiveBasketImg,
    oliveFlounderFilletSpecsImg,
    oliveFlounderFilletPackImg,
    oliveFlounderEpsPackImg,
  ],
};

// NEEDS A PHOTO CHECK: the catalogue entry described Argyrosomus regius but was
// named Sciaena umbra throughout, and this asset is still filed under
// `brown_meagre` on the Odoo side. Nobody has confirmed which fish it actually
// shows. Verify before this page is promoted.
const meagre = {
  url: odooAssetUrl(
    "/web/image/8933-f0ca00b6/customized_d678439dec7544b2a9dca505d6cf86fb_brown_meagre_edited_72d89622-98a2-4f05-8dbd-6ba6dea548f9.webp",
  ),
};

const amberjack = {
  url: odooAssetUrl(
    "/web/image/8934-e1f13b3d/customized_d609f1c9314a409bb53bb323d57a9f54_amberjack_3c6a5d1b-0622-4a86-acbf-d70dc78ddd23.webp",
  ),
  gallery: [amberjackImg],
};

const rainbowTrout = {
  url: odooAssetUrl(
    "/web/image/8925-5db3df07/customized_alabal_edited_cd262b88-b15a-427c-a7b5-03f89c23bd6c.webp",
  ),
};

const shrimp = {
  url: shrimpImg,
  gallery: [shrimpFrozenBoxImg, shrimpFrozenBlockImg, shrimpFrozenCartonsImg],
};

const oyster = { url: oysterImg };

export const CATEGORIES: ProductCategory[] = [
  {
    slug: "live-feed-aquaculture",
    title: "Live Feed for Aquaculture",
    description:
      "Artemia cysts and Chlorella microalgae — the foundation of every larval rearing programme.",
    image: reviveCystsThumbSm,
    imageSrcSet: `${reviveCystsThumbSm} 400w, ${reviveCysts.thumbnail} 800w`,
  },
  {
    slug: "hatchery-solutions",
    title: "Hatchery Solutions & Inputs",
    description:
      "Certified fertilized Atlantic and Coho salmon eggs from a diversified network of international breeding programmes, plus essential hatchery feed inputs like decapsulated Artemia.",
    image: ovaMacroThumbSm,
    imageSrcSet: `${ovaMacroThumbSm} 400w, ${ovaMacroThumbLg} 800w`,
  },
  {
    slug: "seafood",
    title: "Seafood Export",
    description:
      "Premium Mediterranean and farmed seafood — sea bass, sea bream, amberjack, trout, flounder and more — shipped fresh from Turkey to global markets.",
    image: seaBreamThumbSm,
    imageSrcSet: `${seaBreamThumbSm} 400w, ${seaBreamThumbLg} 800w`,
  },
  {
    slug: "feed-additives",
    title: "Additives & Raw Materials",
    description:
      "High-protein feed ingredients and raw materials — fish meal, vital wheat gluten and potato protein — engineered for aquaculture growth, digestibility and pellet stability.",
    image: fishMealThumbSm,
    imageSrcSet: `${fishMealThumbSm} 400w, ${fishMealThumbLg} 800w`,
  },
];

export const PRODUCTS: Product[] = [
  // ────────────────── Feed Additives ──────────────────
  {
    slug: "fish-meal",
    category: "feed-additives",
    title: "Fish Meal",
    tagline:
      "High-protein, highly digestible marine ingredient essential for optimal growth, health and feed efficiency.",
    tags: ["Protein 55–65%", "Sustainably sourced", "Bulk & 25 kg"],
    description:
      "A key ingredient in aquaculture feed production. At VARS Aquaculture, our fish meal is produced from sustainably sourced whole fish and trimmings, ensuring consistent quality, strong nutritional value and reliable supply.",
    longDescription: [
      "Fish Meal is one of the foundational marine protein sources in industrial aquaculture feed. Produced from wild-caught pelagic species and trimmings under strict biosecurity protocols, it provides an exceptional natural amino acid profile, high biological value, and strong palatability across carnivorous finfish and crustacean diets.",
      "Steam-drying at controlled low temperatures preserves heat-sensitive amino acids (especially lysine and methionine) and maintains crude protein between 55% and 65%, with total lipids held at 8–12% and moisture under 10%. This gentle thermal profile ensures pepsin digestibility exceeding 90% and keeps biogenic amines (histamine and total volatile nitrogen / TVN) at fresh, stable baselines.",
      "Rich in long-chain Omega-3 polyunsaturated fatty acids (EPA and DHA) as well as bioavailable calcium and phosphorus, it supports skeletal development, immune resilience, and rapid growth in salmonid, sea bass, sea bream, and shrimp operations. Consignments are packed in 25 kg bags or bulk big-bags, fully tested and certified for global export.",
    ],
    highlights: [
      "Sustainably sourced whole fish & trimmings",
      "Consistent nutritional profile batch-to-batch",
      "Global shipping via air and sea freight",
      "Flexible Incoterms (EXW, FOB, CIF)",
    ],
    metrics: [
      { label: "Moisture", value: "≤ 10%" },
      { label: "Protein", value: "55–65%" },
      { label: "Lipids (Fat)", value: "8–12%" },
      { label: "Ash (Minerals)", value: "10–20%" },
    ],
    specifications: [
      { label: "Product Type", value: "Steam-dried fish meal" },
      { label: "Packaging", value: "25 kg bags · big bags · bulk · export-ready packaging" },
      { label: "Storage", value: "Cool, dry, ventilated; protect from moisture & sunlight" },
      { label: "Delivery", value: "Global air & sea freight · palletized or bulk" },
      { label: "Incoterms", value: "EXW · FOB · CIF" },
    ],
    applications: [
      "Salmonid grow-out feeds",
      "Sea bream & sea bass diets",
      "Shrimp and prawn nutrition",
      "Fry starter and broodstock formulations",
    ],
    storage:
      "Store in cool, dry and ventilated conditions. Protect from moisture and direct sunlight. Keep in sealed packaging.",
    advantages: [
      {
        title: "Sustainably sourced raw material",
        desc: "Produced from sustainably sourced whole fish and trimmings, which is what holds the nutritional profile steady from one lot to the next.",
      },
      {
        title: "Consistent quality and reliable supply",
        desc: "Strong nutritional value and continuity of supply come from a producer network rather than a single mill, so a formulation built on one delivery still works on the next.",
      },
      {
        title: "Flexible Incoterms",
        desc: "EXW, FOB and CIF, in bulk or palletised, with dispatch as soon as the lot is allocated.",
      },
      {
        title: "Global freight, air or sea",
        desc: "Routed by air where the schedule is tight and by sea where the volume makes it economic.",
      },
    ],
    deliveryOptions: ["Air freight", "Sea freight", "Bulk shipment", "Palletised shipment"],
    image: fishMeal.url,
    seoKeywords: [
      "fish meal wholesale",
      "aquafeed fish meal",
      "steam dried fish meal",
      "fish protein powder",
      "digestible marine protein",
      "shrimp feed fish meal",
    ],
    searchSynonyms: [
      "fishmeal",
      "fish powder",
      "marine protein",
      "pelagic fish meal",
      "anchovy meal",
      "aquafeed protein",
    ],
    relatedProducts: ["vital-wheat-gluten", "potato-protein-meal"],
  },
  {
    slug: "vital-wheat-gluten",
    category: "feed-additives",
    title: "Vital Wheat Gluten",
    tagline:
      "High-performance plant-based protein for aquafeed — exceptional binding, digestibility and pellet stability.",
    tags: ["Protein ≥ 82%", "Water absorption ≥ 150%", "EU / Canada origin"],
    description:
      "Vital Wheat Gluten is a high-performance plant protein designed for aquaculture feed formulations. It delivers exceptional binding properties, high digestibility and consistent pellet quality across hatchery, grow-out and broodstock feeds.",
    longDescription: [
      "At VARS Aquaculture, our vital wheat gluten is sourced from certified European and Canadian producers and processed to ensure high protein concentration (≥ 82% dry basis) and superior functional performance in extruded aquafeed.",
      "When hydrated during extrusion conditioning, gluten proteins (gliadin and glutenin) form an elastic, cohesive viscoelastic network that binds starches and lipids securely. This structural matrix provides exceptional water stability and pellet durability, dramatically reducing underwater nutrient leaching, physical breakdown, and fine particulate pollution in aquaculture tanks and open cages.",
      "With water absorption capacity exceeding 150% and pepsin digestibility above 90%, it serves as a cost-effective, highly digestible plant protein complementing marine meals for carnivorous marine species, salmonids, and shrimp. It is supplied in 25 kg multi-wall bags and bulk containers, fully certified for commercial feed manufacturing.",
    ],
    highlights: [
      "Protein content ≥ 82% (dry basis)",
      "Excellent binding and pellet stability",
      "High water absorption capacity ≥ 150%",
      "Improves feed efficiency and digestibility",
      "Suitable for hatchery, grow-out and broodstock feeds",
    ],
    metrics: [
      { label: "Water Absorption", value: "150%" },
      { label: "Protein", value: "~82%" },
      { label: "Moisture", value: "10%" },
      { label: "Calcium", value: "250 mg" },
    ],
    specifications: [
      { label: "Product Type", value: "Vital Wheat Gluten" },
      { label: "Origin", value: "EU / Canada (depending on batch)" },
      { label: "Protein (dry basis)", value: "≥ 82%" },
      { label: "Moisture", value: "≤ 10%" },
      { label: "Ash", value: "≤ 2.0%" },
      { label: "Water Absorption", value: "≥ 150%" },
      { label: "Form", value: "Fine powder (yellowish / cream)" },
      { label: "Packaging", value: "25 kg bags · big bags / bulk supply" },
      { label: "Shelf Life", value: "2–3 years under recommended storage" },
    ],
    applications: [
      "Extruded pellet binder",
      "Salmonid and marine finfish diets",
      "Shrimp feed formulations",
      "Broodstock and hatchery feeds",
    ],
    storage:
      "Store in a cool, dry warehouse in sealed packaging. Shelf life 18 months under recommended conditions.",
    advantages: [
      {
        title: "Binding and pellet stability",
        desc: "High water absorption and binding performance hold pellet integrity through handling and feeding, which is the property most formulations buy it for.",
      },
      {
        title: "Origin declared per batch",
        desc: "EU or Canada depending on the batch, stated on the documentation rather than averaged into a single claim.",
      },
      {
        title: "Flexible Incoterms",
        desc: "EXW, FOB and CIF, in bulk or palletised, with fast order processing and dispatch.",
      },
      {
        title: "Global freight, air or sea",
        desc: "Shipped worldwide, routed to suit the volume and the delivery window.",
      },
    ],
    deliveryOptions: ["Air freight", "Sea freight", "Bulk shipment", "Palletised shipment"],
    image: wheatGluten.url,
    seoKeywords: [
      "vital wheat gluten",
      "wheat gluten 82%",
      "aquafeed binder",
      "extruded feed protein",
      "high protein wheat gluten",
      "water stability binder",
    ],
    searchSynonyms: [
      "wheat gluten",
      "gluten 82",
      "pellet binder",
      "aquafeed protein",
      "plant protein binder",
      "gluten powder",
    ],
    relatedProducts: ["potato-protein-meal", "fish-meal"],
  },
  {
    slug: "potato-protein-meal",
    category: "feed-additives",
    title: "Potato Protein Meal",
    tagline:
      "Concentrated plant protein meal for seabass, seabream and salmonids — high digestibility, low anti-nutritional factors.",
    tags: ["Protein ≥ 76%", "Digestibility 92%", "EU Origin"],
    description:
      "Potato Protein Meal is a premium, highly concentrated plant protein ingredient specifically formulated for marine aquaculture feeds. It offers superior amino acid balance, high digestibility and low anti-nutritional factors, making it an ideal alternative to fish meal in seabass and seabream diets.",
    longDescription: [
      "Potato Protein Meal is manufactured from non-GMO European potatoes through precise extraction and drying processes that preserve amino acid integrity.",
      "Extensive trial data demonstrates that replacing up to 20% of fish meal with Potato Protein Meal maintains optimal growth rates and feed conversion ratios (FCR) while reducing diet costs.",
      "Potato protein concentrate is a co-product of potato starch manufacture. Once the starch has been separated, the potato fruit juice left behind still carries the tuber's soluble protein; that protein is recovered, concentrated and dried into the meal supplied here. Nothing is grown specifically to produce it, which is part of why it sits alongside marine protein in a formulation rather than competing with food-crop acreage for the privilege.",
      "Formulators reach for it where a plant protein has to behave like an animal one. Its amino acid profile is unusually well balanced for a vegetable source — lysine and methionine are the two that most often limit plant proteins in marine diets — and the low fibre and ash content mean it concentrates protein without diluting the digestible energy of the finished pellet.",
      "In practice it is a partial substitution rather than a wholesale one. It displaces a share of the marine protein while the remainder of the formulation continues to supply the long-chain fatty acids that no plant protein carries. Inclusion is set against the rest of the diet and confirmed on the analysis for the lot you are quoted, and the meal ships in 25 kg bags or 1,000 kg big bags depending on how it is dosed at the mill.",
    ],
    highlights: [
      "Crude Protein ≥ 76% (dry basis)",
      "High digestibility coefficient ≥ 92%",
      "Rich in essential amino acids (Lysine & Methionine)",
      "Non-GMO European origin",
      "Reduces dependency on fish meal in marine diets",
    ],
    metrics: [
      { label: "Protein", value: "≥ 76%" },
      { label: "Digestibility", value: "92%" },
      { label: "Moisture", value: "≤ 8%" },
      { label: "Fat", value: "≤ 2%" },
    ],
    specifications: [
      { label: "Product Type", value: "Concentrated Potato Protein Powder" },
      { label: "Origin", value: "European Union" },
      { label: "Packaging", value: "25 kg bags · 1000 kg big bags" },
      { label: "Shelf Life", value: "24 months in dry conditions" },
    ],
    applications: [
      "Seabass & Seabream grow-out diets",
      "Salmonid feeds",
      "Shrimp larval & juvenile feeds",
    ],
    storage:
      "Store in a cool, dry, ventilated warehouse. Avoid humidity — the material is hygroscopic. Protect from direct sunlight and keep in sealed packaging.",
    advantages: [
      {
        title: "Reduces fish-meal dependency",
        desc: "A high-digestibility plant protein that displaces part of the marine protein in marine diets without giving up amino acid balance.",
      },
      {
        title: "Non-GMO European origin",
        desc: "Origin and status travel with the consignment documentation.",
      },
      {
        title: "Specified against your formulation",
        desc: "Protein, ash, fibre and digestibility are confirmed on the analysis for the lot you are quoted, not a brochure typical.",
      },
    ],
    image: fishMeal.url,
    seoKeywords: [
      "potato protein meal",
      "potato protein powder",
      "plant protein aquafeed",
      "fish meal replacer",
      "high digestibility potato protein",
    ],
    searchSynonyms: [
      "potato protein",
      "potato feed powder",
      "concentrated potato protein",
      "non gmo plant protein",
    ],
    relatedProducts: ["fish-meal", "vital-wheat-gluten", "enzyfish-coral"],
  },
  {
    slug: "enzyfish-coral",
    category: "feed-additives",
    title: "Marine Hydrolyzed Fish Protein (Coral)",
    tagline:
      "Bioactive marine pelagic fish protein hydrolysate powder (72–76% protein) for high-performance aquafeed and premium pet food.",
    tags: ["Protein 72–76%", "Pepsin Digestibility > 95%", "Water Solubility > 80%"],
    description:
      "Marine Hydrolyzed Fish Protein (Coral Grade) is an enzymatically hydrolyzed pelagic fish protein powder produced from select wild pelagic fish (sardine, mackerel, horse mackerel, anchovy). With 72–76% crude protein, >95% pepsin digestibility, and >80% water solubility, it provides instantaneous amino acid uptake, intense feeding attractancy for marine fry and shrimp post-larvae, and hypoallergenic protein integrity.",
    longDescription: [
      "This marine pelagic fish hydrolysate is engineered through controlled enzymatic hydrolysis of fresh pelagic fish co-products (sardine, mackerel, anchovy), breaking whole marine proteins down into short-chain bioactive peptides and free amino acids. Unlike conventional fish meals subjected to aggressive thermal processing, this gentle biocatalytic cleavage preserves essential amino acid integrity and delicate marine Omega-3 fatty acids.",
      "In aquaculture, it functions as a powerful natural attractant and gut accelerator for marine fish larvae (seabass, seabream, turbot) and shrimp post-larvae. It stimulates active chemoreceptive feeding strikes, accelerates intestinal villi development, and significantly improves early survival and uniform growth during critical weaning transitions.",
      "For pet food manufacturers, it delivers a premium, hypoallergenic marine peptide profile. Its low molecular weight prevents IgE-mediated allergic reactions, making it ideal for specialized elimination diets, hydrolysate cat kibble (4–5% inclusion), and premium functional palatability coatings.",
    ],
    highlights: [
      "High crude protein: 72–76% (typical 74%) with exceptional water solubility (> 80%)",
      "Controlled peptide distribution: 85% < 2,000 Da and 95% < 5,000 Da for immediate assimilation",
      "Superior in vivo and pepsin digestibility (> 95%) for near-total nutrient absorption",
      "Guaranteed freshness and biosecurity: TVN < 300 mg/100g and biogenic amines < 1,000 ppm",
      "Sustainably sourced from wild pelagic fisheries (sardine, mackerel, horse mackerel, anchovy)",
    ],
    metrics: [
      { label: "Crude Protein", value: "72–76%" },
      { label: "Digestibility", value: "> 95%" },
      { label: "Water Solubility", value: "> 80%" },
      { label: "Peptides < 2 kDa", value: "85%" },
    ],
    specifications: [
      { label: "Product Form", value: "Fine spray-dried powder (light brown)" },
      { label: "Crude Protein", value: "72–76% (typical 74%)" },
      { label: "Crude Fat", value: "8–15% (rich in Omega-3 EPA/DHA)" },
      { label: "Ash (Minerals)", value: "< 10%" },
      { label: "Moisture", value: "< 5%" },
      { label: "Pepsin Digestibility", value: "> 95%" },
      { label: "Soluble Protein", value: "> 80% (high water solubility)" },
      { label: "Molecular Weight", value: "85% < 2,000 Da · 95% < 5,000 Da" },
      { label: "Freshness (TVN)", value: "< 300 mg/100g (biogenic amines < 1,000 ppm)" },
      { label: "Packaging", value: "25 kg paper bags with PE inner liner · 1000 kg big bags" },
      { label: "Shelf Life", value: "24 months in original sealed packaging" },
    ],
    applications: [
      "Marine finfish starter & weaning feeds (seabass, seabream, turbot)",
      "Shrimp nursery & post-larval diets (1–5% inclusion)",
      "Hypoallergenic dog & cat diets (elimination kibble 4–5%)",
      "Wet pet food gravies & liquid palatability coatings",
      "Functional larval weaning feeds and micro-diets",
    ],
    storage:
      "Store in a cool, dry, well-ventilated warehouse below 25 °C. Protect from humidity and direct sunlight. Keep original bag tightly sealed after opening.",
    advantages: [
      {
        title: "Enzymatic hydrolysis, not heat-rendering",
        desc: "Produced via gentle biocatalytic enzymatic cleavage rather than aggressive heat rendering, preserving peptide bioactivity, digestibility, and natural flavour nucleotides.",
      },
      {
        title: "Immediate cellular absorption",
        desc: "Over 85% of peptides are below 2,000 Da, enabling direct peptide-transporter (PepT1) absorption without requiring energy-intensive digestive breakdown.",
      },
      {
        title: "Intense chemosensory attraction",
        desc: "Rich in free amino acids and low-molecular marine peptides that trigger immediate gustatory and olfactory feeding stimulation in early-stage aquatic species.",
      },
      {
        title: "Clean hypoallergenic profile",
        desc: "Short peptide chains bypass immune recognition in companion animals, providing functional hypoallergenic protein for veterinary and sensitive diets.",
      },
    ],
    deliveryOptions: [
      "Air freight for priority batches",
      "Sea freight (FCL & LCL)",
      "Palletised 25 kg export bags",
      "1,000 kg big bags for automated mills",
    ],
    image: enzyfishCoral.url,
    thumbnail: enzyfishCoral.thumbnail,
    gallery: enzyfishCoral.gallery,
    seoKeywords: [
      "fish protein hydrolysate",
      "marine peptide powder",
      "enzymatic fish hydrolysate",
      "hypoallergenic pet food protein",
      "shrimp feed attractant hydrolysate",
      "aquafeed bioactive peptides",
      "digestible fish peptides",
      "Marine Hydrolyzed Fish Protein (Coral)",
    ],
    searchSynonyms: [
      "fish hydrolysate",
      "marine peptides",
      "fish peptide powder",
      "hydrolyzed fish protein",
      "FPH",
      "bioactive fish peptides",
      "soluble fish protein",
    ],
    relatedProducts: ["enzyfish-silver", "enzyfish-gold", "fish-meal"],
  },
  {
    slug: "enzyfish-silver",
    category: "feed-additives",
    title: "Hydrolyzed Salmon Protein (Silver)",
    tagline:
      "Single-species salmon bio-protein hydrolysate for premium pet food and high-performance salmonid starter diets.",
    tags: ["Protein ≥ 62%", "Salmon Bio-Protein", "Peptides < 500 Da 69.5%"],
    description:
      "Hydrolyzed Salmon Protein (Silver Grade) is an enzymatically hydrolyzed bio-protein powder sourced exclusively from Norwegian Atlantic salmon. Rich in natural bioactive peptides with nearly 70% under 500 Da, it offers superior bioavailability, an authentic salmon flavour profile, and high palatability for high-end companion animal nutrition and salmonid aquafeeds.",
    longDescription: [
      "This salmon bio-protein hydrolysate is manufactured exclusively from sustainably sourced Atlantic salmon (Salmo salar) co-products using precise controlled enzymatic hydrolysis. This mono-species origin guarantees full batch traceability, an unadulterated salmon fatty-acid and peptide profile, and clean organoleptic qualities.",
      "With 69.5% of peptides under 500 Da and 42.7% under 150 Da, this hydrolysate delivers an exceptional concentration of free amino acids and di/tri-peptides. This allows rapid intestinal uptake without digestive fatigue, making it particularly valuable for newly weaned animals, young carnivorous fish, and animals recovering from intestinal stress.",
      "In companion animal nutrition, it allows pet food brands to substantiate authentic single-source 'Salmon Protein Hydrolysate' label claims. Its natural umami richness acts as a powerful non-synthetic palatant, driving appetite in fussy cats and convalescent dogs while supporting skin, coat, and gut mucosal health.",
    ],
    highlights: [
      "100% single-species Atlantic salmon bio-protein source",
      "Crude protein min. 62% with > 60% soluble protein fraction",
      "69.5% molecular weight < 500 Da for immediate intestinal assimilation",
      "Natural salmon umami profile functioning as a high-potency palatant",
      "Excellent batch-to-batch consistency and complete Norwegian origin traceability",
    ],
    metrics: [
      { label: "Crude Protein", value: "≥ 62%" },
      { label: "Digestibility", value: "> 93%" },
      { label: "Peptides < 500 Da", value: "69.5%" },
      { label: "Soluble Protein", value: "> 60%" },
    ],
    specifications: [
      { label: "Product Form", value: "Fine spray-dried powder (grey-gold)" },
      { label: "Origin Species", value: "Atlantic Salmon (Salmo salar)" },
      { label: "Crude Protein", value: "Min. 62% (typical 62–64%)" },
      { label: "Crude Fat", value: "4–6% (typical 5.0%)" },
      { label: "Ash (Minerals)", value: "8–9% (typical 8.2%)" },
      { label: "Moisture", value: "7–8% (typical 7.2%)" },
      { label: "Pepsin Digestibility", value: "> 93% (typically 94–95%)" },
      { label: "Soluble Protein", value: "> 60%" },
      { label: "Molecular Weight", value: "69.5% < 500 Da · 42.7% < 150 Da" },
      { label: "pH Range", value: "5.5 – 6.5" },
      { label: "Packaging", value: "25 kg paper bags with PE liner · 1000 kg big bags" },
      { label: "Shelf Life", value: "24 months in original sealed packaging" },
    ],
    applications: [
      "Premium salmon-claim cat and dog dry kibble",
      "Wet pet food, pouches, and lickable treats",
      "Salmonid starter and smolt transfer feeds",
      "Early weaning piglet feed formulations (2–4% inclusion)",
      "High-digestibility calf milk replacers and veterinary diets",
    ],
    storage:
      "Store in a cool, dry, well-ventilated warehouse below 25 °C. Protect from humidity, heat sources and direct sunlight. Keep bags sealed after opening.",
    advantages: [
      {
        title: "100% single-species salmon origin",
        desc: "Derived exclusively from Atlantic salmon, ensuring consistent fatty-acid and peptide profiles that support premium clean-label pet food claims.",
      },
      {
        title: "Nearly 70% short-chain peptides",
        desc: "With 69.5% below 500 Da, peptides bypass standard protease breakdown and are directly absorbed via intestinal peptide transporters.",
      },
      {
        title: "Authentic salmon palatability",
        desc: "Natural salmon peptides and nucleotide precursors deliver a strong, authentic umami aroma that drives immediate feed acceptance in cats and dogs.",
      },
      {
        title: "Young animal digestive support",
        desc: "Gentle on immature gut microbiomes, significantly reducing post-weaning diarrhoea and accelerating villi maturation in piglets and fry.",
      },
    ],
    deliveryOptions: [
      "Air freight for urgent requirements",
      "Sea freight (FCL & LCL)",
      "Palletised 25 kg export bags",
      "1,000 kg big bags for industrial compounding",
    ],
    image: enzyfishSilver.url,
    thumbnail: enzyfishSilver.thumbnail,
    gallery: enzyfishSilver.gallery,
    seoKeywords: [
      "salmon protein hydrolysate",
      "salmon peptide powder",
      "salmon bio protein",
      "hydrolyzed salmon pet food",
      "salmon peptide palatant",
      "salmonid starter hydrolysate",
      "piglet salmon peptides",
      "Hydrolyzed Salmon Protein (Silver)",
    ],
    searchSynonyms: [
      "salmon hydrolysate",
      "salmon peptides",
      "salmon protein powder",
      "hydrolyzed salmon",
      "salmon bio-protein",
      "salmon viscera hydrolysate",
    ],
    relatedProducts: ["enzyfish-coral", "enzyfish-gold", "fish-meal"],
  },
  {
    slug: "enzyfish-gold",
    category: "feed-additives",
    title: "Hydrolyzed Shrimp Protein (Gold)",
    tagline:
      "Microgranulated hydrolyzed shrimp protein (≥62% protein) with bioactive peptides (<1,200 Da) for aquaculture and young animal feeds.",
    tags: ["Protein ≥ 62%", "Pepsin Digestibility > 94%", "Protein Solubility ≥ 92%"],
    description:
      "Hydrolyzed Shrimp Protein (Gold Grade) is an advanced microgranulated hydrolyzed shrimp protein powder produced via controlled enzymatic hydrolysis of fresh shrimp by-products. With ≥62% crude protein, >94% pepsin digestibility, and ≥92% protein solubility, it provides bioactive low molecular weight peptides (<1,200 Da), high feeding attractancy, and proven immunomodulating benefits.",
    longDescription: [
      "This microgranulated shrimp protein hydrolysate concentrates the most biologically active components of marine crustaceans into a fine, highly soluble peptide powder. Produced via mild enzymatic hydrolysis of fresh shrimp processing streams, it preserves functional bio-molecules that are typically destroyed by conventional rendering.",
      "The peptide profile is rich in bioactive sequences below 1,200 Da, delivering near-instant intestinal mucosal absorption. Furthermore, its specialized marine amino acid profile (rich in arginine, leucine, and lysine) and natural antimicrobial peptides provide proven immune priming and cellular protection against pathogenic stress.",
      "In aquaculture, it serves as a premier attractant and survival booster for Penaeus vannamei and P. monodon hatcheries and nurseries, as well as marine fish starters, while offering an efficient replacement for squid meal and krill meal. In terrestrial livestock, inclusion at 2–5% in early piglet diets provides a cost-effective alternative to blood plasma while stimulating energetic feed consumption.",
    ],
    highlights: [
      "High protein concentration: min. 62% crude protein with min. 92% protein solubility",
      "Low molecular weight bioactive peptides (< 1,200 Da) for rapid mucosal assimilation",
      "Exceptional pepsin digestibility (> 94%) reducing nitrogen excretion and tank fouling",
      "Natural immunomodulating and antimicrobial activity strengthening pathogen defense",
      "Cost-effective functional replacement for squid meal, krill meal, and prime fish meal",
    ],
    metrics: [
      { label: "Crude Protein", value: "≥ 62%" },
      { label: "Digestibility", value: "> 94%" },
      { label: "Protein Solubility", value: "≥ 92%" },
      { label: "Bioactive Peptides", value: "< 1,200 Da" },
    ],
    specifications: [
      { label: "Product Form", value: "Microgranulated spray-dried powder (warm amber-brown)" },
      { label: "Raw Material", value: "Fresh shrimp by-products (Penaeus vannamei)" },
      { label: "Crude Protein", value: "Min. 62.0% (guaranteed)" },
      { label: "Crude Fat", value: "Max. 10.0%" },
      { label: "Ash (Minerals)", value: "Max. 18.0%" },
      { label: "Moisture", value: "Max. 6.0%" },
      { label: "Minerals (Ca / P)", value: "Calcium ≥ 0.80% · Phosphorus ≥ 0.27%" },
      { label: "Sodium & Salt", value: "Sodium ≥ 1.40% · NaCl ≤ 4.10%" },
      { label: "Essential Amino Acids", value: "Lysine ≥ 3.5% · Leucine ≥ 3.0% · Arginine ≥ 2.6%" },
      { label: "Pepsin Digestibility", value: "Min. 94.0% (0.02% pepsin)" },
      { label: "Protein Solubility", value: "Min. 92.0%" },
      { label: "Molecular Weight", value: "Bioactive peptides < 1,200 Da" },
      { label: "Packaging", value: "25 kg paper bags with PE liner · 1000 kg big bags" },
      { label: "Shelf Life", value: "24 months in original sealed packaging" },
    ],
    applications: [
      "Penaeid shrimp hatchery, nursery & grow-out diets (1.5–3.0% inclusion)",
      "Marine fish larval micro-diets (attractant & immune priming)",
      "Early-weaned piglet feeds (2–5% inclusion as plasma/fish meal alternative)",
      "Poultry pre-starter feed (1–2% inclusion for gut integrity)",
      "Commercial aquaculture attractant coatings and top-dressings",
    ],
    storage:
      "Store in cool, dry conditions below 25 °C away from moisture and direct sunlight. Re-seal bag tightly after use to prevent ambient moisture absorption.",
    advantages: [
      {
        title: "Bioactive peptides (< 1,200 Da)",
        desc: "Delivers low molecular weight peptides for rapid mucosal absorption and metabolic efficiency in post-larval diets.",
      },
      {
        title: "Direct squid and krill meal replacer",
        desc: "Provides comparable or superior chemo-attractancy and amino acid balance at a significantly better cost-performance ratio.",
      },
      {
        title: "Antimicrobial & immune defense",
        desc: "Contains natural crustacean bioactive sequences that prime gut immunity and create an unfavorable environment for pathogenic Vibrio.",
      },
      {
        title: "Irresistible feeding stimulant",
        desc: "Loaded with marine nucleotides, betaine, and free amino acids that trigger aggressive feeding behavior, reducing feed waste and improving tank FCR.",
      },
    ],
    deliveryOptions: [
      "Air freight for urgent hatchery cycles",
      "Sea freight (FCL & LCL)",
      "Palletised 25 kg multi-wall export bags",
      "1,000 kg big bags for industrial aquafeed mills",
    ],
    image: enzyfishGold.url,
    thumbnail: enzyfishGold.thumbnail,
    gallery: enzyfishGold.gallery,
    seoKeywords: [
      "shrimp peptide hydrolysate",
      "hydrolyzed shrimp protein",
      "shrimp feed attractant",
      "astaxanthin peptide powder",
      "shrimp cholesterol feed additive",
      "piglet plasma replacer peptide",
      "crustacean hydrolysate powder",
      "Hydrolyzed Shrimp Protein (Gold)",
    ],
    searchSynonyms: [
      "shrimp hydrolysate",
      "shrimp peptide powder",
      "hydrolyzed shrimp heads",
      "shrimp meal hydrolysate",
      "crustacean peptides",
      "shrimp protein powder",
    ],
    relatedProducts: ["enzyfish-coral", "enzyfish-silver", "fish-meal"],
  },
  // ────────────────── Hatchery Solutions ──────────────────
  {
    slug: "atlantic-salmon-egg",
    category: "hatchery-solutions",
    title: "Atlantic Fertilized Salmon Eggs",
    latinName: "Salmo salar",
    tagline:
      "High-quality fertilized Atlantic salmon eggs sourced from a diversified network of trusted international hatcheries.",
    tags: ["Disease-free broodstock", "Diploid & Triploid", "Cold-chain air freight"],
    description:
      "At VARS Aquaculture we supply high-quality fertilized Atlantic Salmon (Salmo salar) eggs sourced from a diversified network of trusted international hatcheries. Our multi-origin model ensures continuous availability, stable pricing and fast, temperature-controlled air shipment to your destination.",
    longDescription: [
      "Build a reliable salmon production cycle with carefully selected fertilized eggs produced under strict hatchery and biosecurity standards.",
      "Start your Atlantic salmon lifecycle with consistency and confidence. Our eggs are sourced from certified, disease-free broodstock maintained under stringent biosecurity protocols.",
      "Each batch is carefully selected for high fertilization rates, uniform size and strong resistance traits. Whether the destination is a seawater or a freshwater production system, the consignment is prepared to support strong hatchability, healthy fry development and consistent early-stage performance.",
    ],
    // The batch selection criteria the retired page published under "High
    // Survival, Proven Genetics". The list that used to sit here was a mix of
    // the ploidy options and the applications array, so it duplicated four
    // entries of `applications` and told a buyer nothing new.
    highlights: [
      "Diploid and triploid eyed ova available",
      "Every batch selected on fertilization quality",
      "Uniform egg grading and hatch consistency",
      "Genetic stability across production batches",
      "Adaptability to the destination farming environment",
    ],
    metrics: [
      // Hatch rate and SPF status are the source hatchery's certified figures,
      // confirmed by the owner 2026-08-17. The certificate holder is named in
      // the specification row below, per ADR on whose certificate it is.
      { label: "Hatching Rate", value: "≥ 95%" },
      { label: "Broodstock", value: "SPF certified" },
      { label: "Ploidy", value: "Diploid / Triploid" },
      { label: "Delivery", value: "4 °C air" },
    ],
    specifications: [
      { label: "Species", value: "Atlantic Salmon (Salmo salar)" },
      // Whose certificate it is, not just that one exists.
      {
        label: "Broodstock Health",
        value: "Disease-free and SPF, certified by the source hatchery",
      },
      { label: "Origins", value: "Certified hatcheries across Europe and North America" },
      { label: "Shipping", value: "Insulated boxes, temperature-controlled air freight" },
      { label: "Delivery Temp.", value: "4–8 °C until transfer to incubation" },
      {
        label: "Packaging",
        value: "EPS insulated boxes with proper oxygenation, for long-distance air transport",
      },
    ],
    seoKeywords: [
      "salmon eyed eggs",
      "salmon ova",
      "fertilized Atlantic salmon eggs",
      "SPF salmon ova",
      "Salmo salar ova",
      "triploid salmon eggs",
      "all female salmon eggs",
      "eyed salmon ova",
      "coldwater incubation ova",
    ],
    searchSynonyms: [
      "eyed eggs",
      "salmon eggs",
      "salmon ova",
      "fertilized eggs",
      "eyed ova",
      "salmon fingerling eggs",
      "salmon hatchery ova",
    ],
    applications: [
      "Commercial salmon hatcheries",
      "Land-based RAS start-ups",
      "Freshwater and seawater grow-out",
      "Research and breeding facilities",
    ],
    storage:
      "Maintain 4–8 °C until transfer to incubation trays. Handle with strict biosecurity protocols.",
    // Ported from the retired product page, which published these under
    // "Product Advantages" and "Why Work With VARS Aquaculture?". Multi-origin
    // supply was the differentiator that page led with and it had survived
    // nowhere on this site except a neutral "Origins" row in the spec table.
    advantages: [
      {
        title: "Multi-origin supply, not a single hatchery",
        desc: "We source from multiple certified producers across Europe and North America, which is what keeps a season workable when one origin is disrupted and keeps availability continuous through peak demand.",
      },
      {
        title: "Genetic excellence",
        desc: "Sourced from certified broodstock programmes selected for health, performance and production reliability.",
      },
      {
        title: "High hatch viability",
        desc: "Handling and packing procedures designed to maintain egg integrity in transit, so what arrives performs like what was stripped.",
      },
      {
        title: "Market-specific sourcing",
        desc: "Eggs matched to your market's regulatory conditions, water parameters and strain requirements, rather than whatever the calendar happens to offer.",
      },
      {
        title: "Fast global air shipment",
        desc: "Temperature-controlled packing, oxygenated insulation and full cold-chain handling with tracking through to delivery.",
      },
      {
        title: "Certified health documentation",
        desc: "Every consignment travels with traceable certification, its disease-free status and the import documentation the destination requires.",
      },
      {
        title: "Technical support included",
        desc: "Guidance from egg reception through to first feeding, not just to the airport.",
      },
    ],
    deliveryOptions: [
      "Airport-to-airport",
      "Door delivery where available",
      "Special handling for research institutions",
      "Routed for the shortest available transit",
    ],
    image: atlanticOva.url,
    gallery: atlanticOva.gallery,
  },
  {
    slug: "coho-salmon-egg",
    category: "hatchery-solutions",
    title: "Coho Fertilized Salmon Eggs",
    latinName: "Oncorhynchus kisutch",
    tagline:
      "SPF certified Coho salmon ova for cold-water hatcheries and RAS farms — rapid early growth and high survival rates.",
    tags: ["SPF Certified", "Rapid Growth", "Temperature Controlled"],
    description:
      "Fertilized Coho Salmon (Oncorhynchus kisutch) eggs sourced from certified pathogen-free broodstock. Renowned for fast early development, high disease resistance, and excellent performance in freshwater RAS and sea-cage grow-out operations.",
    longDescription: [
      "Coho (Oncorhynchus kisutch) is the salmonid hatcheries turn to when the constraint is cold water or a recirculating system rather than open-sea grow-out. It reaches a marketable size faster than Atlantic salmon in freshwater RAS, and it tolerates the temperature range those systems actually run at, which is why Korean and Japanese hatchery programmes have taken it up.",
      "Ova ship at the eyed stage, the point at which the embryo tolerates handling — before that they cannot travel at all. They move by air freight in insulated boxes at chilled temperature with data loggers, and go straight into the incubator on arrival with minimal thermal shock. Handling and incubation guidance travels with the consignment: tempering on arrival, incubation temperature, and the degree-day expectation to hatch for the batch.",
      "Supply follows the source hatchery's stripping and eyeing calendar rather than a stock position, so volumes are best reserved months ahead of the incubation window. Minimum order is around 50,000 eggs, and every consignment carries the health certification its origin authority issues for the species and destination.",
    ],
    highlights: [
      "Certified Specific Pathogen Free (SPF) status",
      "High hatching rate ≥ 95%",
      "Fast early fry development and feeding response",
      "Supplied in temperature-monitored air freight containers",
    ],
    metrics: [
      { label: "Hatching Rate", value: "≥ 95%" },
      { label: "Broodstock", value: "SPF Certified" },
      { label: "Ploidy", value: "Diploid" },
      { label: "Delivery Temp.", value: "4 °C" },
    ],
    specifications: [
      { label: "Species", value: "Coho Salmon (Oncorhynchus kisutch)" },
      { label: "Health Status", value: "SPF - Free of IPNV, VHSV, IHNV" },
      { label: "Shipping", value: "Temperature-logged insulated shipping boxes" },
    ],
    applications: ["RAS salmon farms", "Cold-water hatcheries", "Commercial salmon production"],
    storage: "Unpack upon arrival into 4-6 °C incubation water following biosecurity protocol.",
    advantages: [
      {
        title: "Built for cold water and RAS",
        desc: "Coho reaches marketable size faster than Atlantic salmon in freshwater recirculating systems and tolerates the temperature range those systems actually run at.",
      },
      {
        title: "Ships at the eyed stage",
        desc: "The point at which the embryo tolerates handling. Before that the ova cannot travel at all, which is why the shipping window follows the hatchery, not the order date.",
      },
      {
        title: "Reserved against the hatchery calendar",
        desc: "Supply follows the stripping and eyeing calendar rather than a stock position, so volumes are best reserved months ahead of the incubation window.",
      },
      {
        title: "Health certification per destination",
        desc: "Every consignment carries the certification its origin authority issues for the species and the destination.",
      },
      {
        title: "Incubation guidance travels with the batch",
        desc: "Tempering on arrival, incubation temperature and the degree-day expectation to hatch, supplied for the specific consignment.",
      },
    ],
    image: cohoOva.url,
    gallery: cohoOva.gallery,
    seoKeywords: [
      "Coho salmon eggs",
      "Oncorhynchus kisutch ova",
      "silver salmon eggs",
      "fertilized Coho ova",
      "eyed Coho salmon eggs",
      "RAS salmon ova",
    ],
    searchSynonyms: [
      "coho ova",
      "silver salmon ova",
      "eyed coho eggs",
      "pacific salmon eggs",
      "coho eggs",
    ],
  },
  {
    slug: "decapsulated-dry-artemia-cysts",
    category: "hatchery-solutions",
    title: "Decapsulated Dry Artemia Cysts",
    tagline:
      "Premium shell-free nutrition — 0% shell contamination, laboratory-grade biosecurity for hatcheries, aquaculture and home aquariums.",
    tags: ["Shell-free", "Ready to feed", "Protein 61%", "Aquarium & Reef Safe"],
    description:
      "Decapsulated Artemia fish fry feed made from shell-free cysts. Check the product preparation instructions and confirm suitability for the species and feeding stage.",
    longDescription: [
      "Decapsulated dry Artemia cysts are non-hatching cysts whose hard, indigestible outer chorion shell has been completely removed through a controlled biological oxidation process. Delivered with 0% shell content, 0% impurities, and moisture controlled at 4–5%, they provide an instant, ready-to-feed live feed substitute.",
      "Because the embryo does not expend metabolic energy breaking through its cyst wall, decapsulated cysts retain 100% of their raw nutritional value — delivering 61% crude protein, 24.5% lipids, and 502 kcal per 100 g. Crucially, removing the shell completely eliminates the biosecurity hazard of shell-borne pathogenic bacteria (including Vibrio spp.) and prevents lethal gut blockages in early larval fish and shrimp.",
      "Suitable for post-larval shrimp (PL1 to PL20), marine fish larvae (seabass, seabream), and ornamental hatchery systems. The dried cysts hydrate quickly in seawater, remaining neutrally buoyant in the water column to stimulate aggressive feeding strikes without requiring aeration cones or hatching infrastructure.",
    ],
    highlights: [
      "Shell-free & ready to use — no hatching required",
      "0% shell content · 0% impurities",
      "Moisture controlled at 4–5%",
      "Improved biosecurity in larval systems",
      "Operational efficiency: no incubation cones needed",
    ],
    metrics: [
      { label: "Protein", value: "~61%" },
      { label: "Fat", value: "~24.5%" },
      { label: "Energy", value: "502 kcal / 100 g" },
      { label: "Moisture", value: "4–5%" },
    ],
    specifications: [
      { label: "Form", value: "Dry decapsulated cysts" },
      { label: "Shell content", value: "0%" },
      { label: "Impurities", value: "0% light · 0% heavy" },
      // No packaging row. The retired product page states none, and the
      // storefront listing reuses the Revive Artemia text verbatim, so it
      // cannot be read as specific to the decapsulated line.
    ],
    applications: [
      "Fish larvae and fry",
      "Shrimp post-larvae",
      "Broodstock conditioning",
      "Ornamental species",
      "Home aquariums & reef tanks",
      "Pet shops & fish breeders",
    ],
    storage: "Store in a cool, dry place. Reseal cans after use to preserve moisture control.",
    advantages: [
      {
        title: "No hatching step at all",
        desc: "Shell-free cysts feed directly, which removes the incubation cones, the separation step and the labour that goes with them.",
      },
      {
        title: "Improved biosecurity in larval systems",
        desc: "No shell means no shell debris in the tank and one less contamination route into a larval system.",
      },
      {
        title: "Full energy retained",
        desc: "Nothing is spent on hatching, so the energy the nauplius would have used reaches the larvae instead.",
      },
      {
        title: "Predictable dosing",
        desc: "A dry, moisture-controlled product doses to a known weight rather than to a variable hatch.",
      },
    ],
    image: reviveDecap.url,
    thumbnail: reviveDecap.thumbnail,
    gallery: reviveDecap.gallery,
    relatedProducts: ["artemia", "emerald-chlorella-powder", "super-fresh-chlorella-v12"],
    seoKeywords: [
      "shell-free Artemia fish fry feed",
      "decapsulated Artemia fish fry feed",
      "shell-free brine shrimp eggs",
      "Kabuksuz Artemia Yavru Balık Yemi",
      "decapsulated Artemia",
      "shell free Artemia",
      "decap Artemia cysts",
      "direct feed Artemia",
      "non hatching Artemia",
      "de-shelled brine shrimp",
      "aquarium decapsulated artemia",
      "reef tank fish food",
      "home aquarium live feed",
      "dekapsüle artemia kisti",
      "kabuksuz artemia yumurtası",
      "dekapsüle artemia",
      "kabuksuz artemia",
      "decapsulated kabuksuz artemia",
      "artemia dekapsüle",
      "dekapsülasyon artemia",
      "kabuksuz artemia yavru yemi",
      "kabuksuz artemia toz yavru yemi",
      "kabuksuz artemia yavru büyütme yemi",
      "artemia yavru yemi",
      "decapsulated brine shrimp eggs",
      "non-hatching brine shrimp eggs",
      "ready-to-feed Artemia",
      "Artemia fry feed",
      "brine shrimp eggs no shell",
    ],
    searchSynonyms: [
      "shell-free Artemia fish fry feed",
      "decapsulated Artemia fish fry feed",
      "Kabuksuz Artemia Yavru Balık Yemi",
      "dekapsüle artemia yumurtası",
      "decap artemia",
      "shell-less artemia",
      "dry decap",
      "non-hatching artemia",
      "shell free brine shrimp",
      "aquarium artemia",
      "reef aquarium food",
      "dekapsüle artemia kisti",
      "kabuksuz artemia yumurtası",
      "dekapsüle artemia",
      "kabuksuz artemia",
      "decapsulated kabuksuz artemia",
      "kabuksuz artemia yumurtasi",
      "artemia dekapsüle",
      "kabuksuz artemia yavru yemi",
      "artemia yavru yemi",
      "decapsulated brine shrimp eggs",
      "ready-to-feed artemia",
      "artemia fry feed",
    ],
  },

  // ────────────────── Live Feed ──────────────────
  {
    slug: "artemia",
    category: "live-feed-aquaculture",
    title: "Revive Artemia Cyst",
    tagline:
      "Premium-grade brine shrimp cysts for hatchery success — proven survival, growth and performance without cold-chain limitations. Also a favorite live feed for home aquariums and reef tanks.",
    tags: [
      "Hatching Rate 85%",
      "Long shelf life",
      "Used across TR · KR · MENA",
      "Aquarium & Reef Ready",
    ],
    description:
      "Revive Artemia Cysts offer high hatch-out efficiency, long shelf life and nutritional consistency for larval-stage fish and shrimp. Backed by global performance data and used by hatcheries across Türkiye, South Korea and the Middle East, this product delivers proven results without cold-chain limitations. The same easy-to-hatch cysts are a trusted staple for public aquariums, pet shops, fish breeders and reef tank hobbyists worldwide.",
    longDescription: [
      "CPG (Number of Cyst per Gram) varies with size and genus of Artemia and is not a quality indicator on its own. Quality is defined by Hatching Rate and by the nutritional value and biomass of the resulting nauplii.",
      "Every batch is hatch-tested before dispatch and typically returns at least 85%, so the figure quoted on your consignment is the figure measured for that lot.",
    ],
    // The only product that had no highlights at all.
    highlights: [
      "Typical hatching rate of at least 85%, batch-tested before dispatch",
      "High hatch-out efficiency and nutritional consistency",
      "Long shelf life with no cold-chain requirement",
      "Used by hatcheries across Türkiye, South Korea and the Middle East",
      "Equally suited to aquarium, pet-shop and reef-tank feeding",
    ],
    metrics: [
      { label: "Hatching Rate", value: "85%" },
      { label: "CPG", value: "210,000" },
      { label: "Protein", value: "55%" },
      { label: "Fat", value: "12%" },
    ],
    specifications: [
      { label: "Product Form", value: "Dry Artemia Cyst" },
      { label: "Typical Hatch Rate", value: "≥ 85%" },
      // The storefront is the sellable catalogue and states the range and
      // the container: "multiple sizes from 50 g up to 2 kg, packed in
      // vacuum-sealed, aluminum-lined bags". The old catalogue row here said
      // cans, which is a different pack entirely.
      { label: "Packaging", value: "50 g – 2 kg vacuum-sealed aluminium-lined bags" },
    ],
    applications: [
      "Marine finfish larvae (sea bass, sea bream, grouper)",
      "Shrimp post-larvae",
      "Ornamental fish larvae",
      "Home aquariums & reef tanks",
      "Public aquariums & pet shops",
    ],
    // No source states a shelf-life figure — the retired page and the
    // storefront both say only "long shelf life". The 36-month number had
    // nothing behind it.
    storage: "Store below 4 °C in sealed packaging, away from moisture and repeated air exposure.",
    advantages: [
      {
        title: "Batch-tested hatching rate",
        desc: "Typically at least 85%, measured per batch before dispatch — that figure is what determines how many nauplii a gram actually delivers.",
      },
      {
        title: "Cyst count is not a quality measure",
        desc: "CPG — cysts per gram — varies with the size and genus of the Artemia and says nothing about quality. Hatching rate and nutritional value are what to compare.",
      },
      {
        title: "No cold-chain dependency",
        desc: "Dry cysts ship and store without refrigerated logistics, so the delivery window is set by freight rather than by temperature.",
      },
      {
        title: "Multi-origin supply, matched to destination",
        desc: "Sourced across origins and matched to the destination's requirements, which is what keeps a season workable when one source is disrupted.",
      },
    ],
    image: reviveCysts.url,
    thumbnail: reviveCysts.thumbnail,
    gallery: reviveCysts.gallery,
    pdfUrl: odooAssetUrl(
      "/web/content/12514?unique=24692c2e204e6066d0f586050b3232512eef996c&download=true&access_token=574dfe3e-ca12-4943-884d-45a1fa2396ea",
    ),
    pdfLabel: "Artemia Cysts Incubation Procedure (PDF)",
    seoKeywords: [
      "Artemia cysts",
      "brine shrimp eggs",
      "Revive Artemia",
      "85% hatch rate Artemia",
      "hatching Artemia cysts",
      "larval live feed",
      "aquarium artemia cysts",
      "reef tank brine shrimp",
      "home aquarium live feed",
      // Turkish buyers search "artemia kisti" even though the Turkish product
      // TITLE says "yumurta" — see docs/project/localization/agents-and-tone.md § Turkish. The
      // search term belongs here so the rename does not cost that traffic.
      "artemia kisti",
      "artemia kistleri",
      "artemia yumurtası",
    ],
    searchSynonyms: [
      "artemia",
      "brine shrimp",
      "hatching cysts",
      "artemia nauplii",
      "aquarium brine shrimp",
      "reef tank live feed",
      "artemia eggs",
      "live larval feed",
      "artemia kisti",
      "artemia kistleri",
      "artemia yumurtası",
    ],
  },
  {
    slug: "emerald-chlorella-powder",
    category: "live-feed-aquaculture",
    title: "Emerald Chlorella Powder",
    tagline:
      "Superior purity and consistent performance — microalgae-based live feed designed for hatcheries and aquaculture professionals.",
    tags: ["Protein 60.6 g", "Vitamin B12 rich", "Immune support"],
    description:
      "Emerald Chlorella Powder is designed specifically for hatcheries and aquaculture professionals seeking a dependable microalgae-based live feed solution. It enhances larval growth, strengthens immune response and promotes higher survival rates.",
    longDescription: [
      "Emerald is a Chlorella vulgaris powder produced in South Korea under controlled conditions, at up to 97% purity. It covers the same nutritional ground as a live culture in the situations where keeping one going is impractical — a hatchery without the tank space, a site that cannot take a cold-chain delivery every week, or a feed mill working to a formulation rather than a culture schedule.",
      "It is used for green-water conditioning and for rotifer culture, where the protein fraction and the pigment load do the work: 60.6 g of protein per 100 g, 12.8 g of fat and 4.5 g of ash, with the chlorophyll and carotene content that gives green water its colour and its function. Rehydrate and dose under continuous aeration; the working rate follows tank volume and rotifer density rather than a single published figure.",
      "The practical advantage over a live concentrate is storage. The powder holds at room temperature below 25 °C in its sealed 2.5 kg foil bag, so it ships by dry container to any destination and keeps without the cold chain a live product depends on. Keep it dark and dry — light and moisture, not time on the shelf, are what degrade it.",
    ],
    highlights: [
      "Protein content 60.6 g / 100 g",
      "Rich in Vitamin B12 (1000 µg/100 g)",
      "Supports rotifer & copepod green-water cultures",
      "Strengthens immune response in marine larvae",
    ],
    metrics: [
      { label: "Protein", value: "60.6 g" },
      { label: "Carbohydrate", value: "15.7 g" },
      { label: "Calories", value: "> 372 kcal" },
      { label: "Vitamin B12", value: "1000 µg/100 g" },
    ],
    specifications: [
      { label: "Form", value: "Spray-dried powder" },
      // 135 g/l is a liquid concentration copied from SV-12 and cannot
      // describe a dry powder. The retired page's own composition table
      // gives 5.4 g per 100 g.
      { label: "Moisture", value: "5.4 g / 100 g" },
      { label: "Vitamin C", value: "74 mg/100 g" },
      { label: "Vitamin D", value: "277.6 µg/100 g" },
      { label: "Vitamin E", value: "22.8 mg/100 g" },
      { label: "Packaging", value: "2.5 kg vacuum-sealed multilayer foil bag" },
      { label: "Species", value: "Chlorella vulgaris" },
      { label: "Origin", value: "South Korea" },
      { label: "Purity", value: "Up to 97% pure Chlorella" },
      { label: "Fat", value: "12.8 g / 100 g" },
      { label: "Ash", value: "4.5 g / 100 g" },
      // Appended, never inserted: these rows are index-keyed into the locale
      // files, so a row added in the middle silently re-points every override
      // after it. See product-locale-alignment.test.ts.
      { label: "Cell size", value: "2–10 µm" },
      { label: "Shelf life", value: "24 months from production" },
      { label: "Producer", value: "Daesang Corporation" },
    ],
    applications: [
      "Rotifer green-water culture",
      "Copepod culture",
      "Larval immune support",
      "Marine hatchery enrichment protocols",
    ],
    // Per the manufacturer (ptaqua.eu/rotifer-cultivation/emerald-rotifer-diet).
    // This said "store below 8 °C after opening", which told a hatchery to
    // refrigerate a product Daesang specifies for dry ambient storage, and it
    // omitted the 60-day open-pack window entirely.
    storage:
      "Keep dark, dry and cool, below 25 °C, in sealed packaging. Consume within 60 days of opening. Refrigerate any prepared suspension and use it within 48 hours.",
    advantages: [
      {
        title: "Axenic culture production",
        desc: "Produced through a patented axenic process, so the powder going into a rotifer tank carries no competing culture with it.",
      },
      {
        title: "Designed for both culture densities",
        desc: "Formulated for high- and low-density rotifer cultivation rather than tuned to one regime.",
      },
      {
        title: "Packed for humidity control",
        desc: "A vacuum-sealed multilayer foil bag, sized for bulk handling and hatchery dosing, which limits contamination and exposure between doses.",
      },
      {
        title: "Documentation included",
        desc: "Shipped from certified distribution points with export documents and technical datasheets.",
      },
    ],
    image: emeraldChlorella.url,
    thumbnail: emeraldChlorella.thumbnail,
    gallery: emeraldChlorella.gallery,
    // "Emerald Rotifer Diet" is the manufacturer's own name for this product.
    seoKeywords: [
      "emerald chlorella powder",
      "emerald rotifer diet",
      "spray dried chlorella",
      "microalgae powder aquafeed",
      "rotifer culture chlorella",
      "rotifer diet",
      "rotifer cultivation powder",
      "green water rotifer culture",
      "L-type rotifer feed",
      "Chlorella pyrenoidosa powder",
    ],
    searchSynonyms: [
      "chlorella powder",
      "dry chlorella",
      "microalgae powder",
      "rotifer green water",
    ],
  },
  {
    slug: "super-fresh-chlorella-v12",
    category: "live-feed-aquaculture",
    title: "Super Fresh Chlorella V12 (SV12)",
    tagline:
      "Live liquid chlorella concentrate — freshly cultured, never dried or frozen, engineered for intensive rotifer culture.",
    tags: ["~13 B cells/ml", "DHA · EPA · B12", "8× rotifers in 3 days"],
    description:
      "Super Fresh Chlorella V12 is specifically formulated for intensive rotifer culture. Each cell is rich in DHA, EPA and vitamin B12, providing superior nutrition. Cultured at the Chikugo plant of Chlorella Industry Co., Ltd. in Fukuoka, Japan, and air-freighted fresh under cold chain — never dried or frozen — SV12 can increase rotifer populations up to eight-fold in three days, leading to healthier rotifers and better fish-larvae survival.",
    longDescription: [
      "Super Fresh Chlorella SV-12 is a live liquid microalgae concentrate developed for intensive rotifer culture, and it is the reference product for hatcheries that measure their live-feed programme rather than run it by habit. It is trusted by commercial-scale hatcheries, research institutes and bio-secure systems, and it is the line behind the Aegean trials that lifted rotifer biomass up to eight-fold in three days.",
      "It is alive, which is the whole specification: never dried, never frozen, with cell walls intact so rotifers can take the cells up directly instead of working through a reconstituted powder. That is what the density and the fresh-cell condition buy — faster population turnover in the culture, and a nutritional profile that carries through the rotifer into the larva.",
      "Because it is live, logistics are part of the product. It ships in a 20-litre insulated cold-chain case under a 2–6 °C chain and is ready to use on arrival, with no mixing step. Keep it cold, avoid temperature swings, and turn the container regularly so the cells stay in suspension.",
    ],
    highlights: [
      "Live cell density: ~13 billion cells / ml",
      "Freshly cultured — never dried or frozen",
      "Up to 8-fold rotifer population growth in 3 days",
      "Rich in DHA, EPA & Vitamin B12",
    ],
    metrics: [
      { label: "Chlorella Density", value: "~13 billion cells / ml" },
      { label: "Cell Size", value: "3–6 µm" },
      { label: "Dry Net Weight", value: "> 135 g/l" },
      { label: "Vitamin B12", value: "> 320 µg/l" },
    ],
    specifications: [
      { label: "Form", value: "Live liquid concentrate" },
      { label: "Preservation", value: "Freshly cultured — never dried or frozen" },
      // 20 L is the only format supplied. The 2 L / 10 L bag-in-box that used
      // to sit here was never a real line, and it contradicted the storefront
      // copy on this same site, which has always said a 20-litre cold-chain
      // case — as do the retired Odoo slugs the redirect map still carries
      // (sv-12-20l, sv12-20-lit-case).
      { label: "Packaging", value: "20 L insulated cold-chain case" },
      { label: "Cold chain", value: "2–6 °C, protected from light" },
    ],
    applications: [
      "Intensive rotifer culture",
      "Copepod feed",
      "Marine hatchery green-water systems",
    ],
    // Handling per the manufacturer datasheet
    // (20240308_PTAqua_SV12_CHLORELLA_03-24.pdf): store < 5 °C, do not freeze,
    // turn regularly, use within 30 days of production. The 30 days is the
    // manufacturer's own instruction — the "45 day shelf life" on their web
    // page contradicts their dated datasheet, so the datasheet governs.
    storage:
      "Store below 5 °C and never freeze. Turn the container regularly during storage, and turn or shake it gently before use so the cells are evenly distributed. Use within 30 days of the production date. Handling and shelf life as specified by the manufacturer.",
    advantages: [
      {
        title: "Never dried, never frozen",
        desc: "Freshly cultured and shipped live under cold chain — the cells arrive intact rather than reconstituted.",
      },
      {
        title: "Formulated for intensive rotifer culture",
        desc: "Each cell is rich in DHA, EPA and vitamin B12, the fractions rotifer nutrition is actually being bought for.",
      },
      {
        title: "Stable, continuous culture",
        desc: "The manufacturer describes rotifer cultivation on SV12 as very stable and well suited to continuous systems, and publishes a dosing schedule for it.",
      },
      {
        title: "Packed for temperature stability",
        desc: "Hard-box cool containers hold the temperature through transit, which is what protects a live product on a long route.",
      },
    ],
    image: sv12.url,
    thumbnail: sv12.thumbnail,
    gallery: sv12.gallery,
    pdfUrl: odooAssetUrl(
      "/web/content/12196?unique=5277fa90217f35f33cb218e8be14e6eaf5099a9e&download=true&access_token=6519fa24-e835-4945-865c-2dadb4e8cba0",
    ),
    pdfLabel: "Super Fresh Chlorella SV-12 Datasheet (PDF)",
    // The manufacturer files this product under /rotifer-cultivation/ and sells
    // it as a rotifer feed. This site sold it as a chlorella product and left
    // the application to be inferred; these terms are all used in the body copy
    // and the protocol tab, so nothing here is a keyword with no page behind it.
    seoKeywords: [
      "Super Fresh Chlorella V12",
      "Chlorella V12",
      "SV12",
      "SV12 chlorella",
      "fresh chlorella from Japan",
      "green water microalgae",
      "rotifer culture feed",
      "rotifer enrichment microalgae",
      "rotifer cultivation",
      "live chlorella for rotifers",
      "S-type rotifer feed",
      "live Chlorella concentrate",
      "Chlorella vulgaris",
    ],
    searchSynonyms: [
      "chlorella",
      "green water",
      "rotifer feed",
      "freshwater chlorella",
      "condensed microalgae",
      "v12 chlorella",
      "sv12",
      "sv-12",
      "japanese chlorella",
      "microalgal concentrate",
    ],
  },

  // ────────────────── Seafood ──────────────────
  {
    slug: "shrimp",
    category: "seafood",
    title: "Shrimp",
    tagline:
      "Clean taste, high-protein and versatile — farmed and wild-caught shrimp with export-grade certifications.",
    tags: ["High-protein · low-fat", "ASC · GlobalG.A.P farms", "Wholesale · HORECA · Retail"],
    description:
      "Shrimp is one of the most demanded seafood products globally, valued for its clean taste, high protein content and versatility across cuisines. At VARS Aquaculture we source shrimp from trusted aquaculture farms and certified fisheries, ensuring premium quality, consistent supply and export-ready standards.",
    longDescription: [
      "Shrimp is one of the most widely traded seafood products in the world, and one of the least forgiving to handle: texture and shelf life are set by how fast it is chilled after harvest, not by the grade on the box. VARS supplies both farmed and wild-caught shrimp, processed and packed to hold condition through a long export leg.",
      "It is a high-protein, low-fat product — 24 g of protein and 99 kcal per 100 g — which is why it moves as readily into retail packs as into hotel kitchens. Formats are agreed per order, and frozen consignments travel by reefer container rather than air, which is materially cheaper per kilo and equally safe once the product is frozen.",
      "Typical minimum for seafood is around 500 kg. Every shipment carries a health or veterinary certificate, a certificate of analysis for the batch, a certificate of origin, and the commercial invoice and packing list; EU-bound consignments are pre-notified through TRACES and Gulf buyers receive SFDA-compliant Halal documentation.",
    ],
    highlights: [
      "Farmed & wild-caught shrimp options",
      "High-protein, low-fat seafood product",
      "Export-grade quality with international certifications",
      "Processed and packed for maximum freshness",
      "Suitable for wholesale, HORECA and retail markets",
    ],
    metrics: [
      { label: "Energy", value: "99 kcal" },
      { label: "Protein", value: "24 g" },
      { label: "PUFA", value: "0.3 g" },
      { label: "MUFA", value: "0.2 g" },
    ],
    specifications: [
      { label: "Standards", value: "Farms certified to ASC / GlobalG.A.P" },
      {
        label: "Formats",
        value: "Whole (H/L) · P&D · Cooked · Tail-on/off · IQF · Fresh/frozen/canned/smoked",
      },
      {
        label: "Size grades",
        value: "Small 100–200/kg · Medium 60–100/kg · Large 30–60/kg · Jumbo 10–30/kg",
      },
      { label: "Packaging", value: "6 kg or 10 kg EPS units" },
      { label: "Availability", value: "52-week production" },
      { label: "SFA", value: "0.2 g" },
      { label: "Salt", value: "0.15 g" },
      { label: "Logistics", value: "Fresh delivery to EMEA & North America" },
    ],
    applications: ["Retail wholesale", "Foodservice / HORECA", "Processing"],
    // Turkish trade name from the tr locale title; no Latin name is claimed
    // because the record does not name a species and guessing one on a
    // product page is a labelling claim, not a keyword.
    seoKeywords: [
      "shrimp wholesale",
      "frozen shrimp export",
      "farmed shrimp supplier",
      "HORECA shrimp supply",
      "block frozen shrimp",
      "karides ihracat",
    ],
    searchSynonyms: [
      "shrimp",
      "prawn",
      "karides",
      "frozen shrimp",
      "jumbo shrimp",
      "tiger shrimp",
      "HOSO",
      "head-on shell-on",
      "vannamei",
      "langostino vannamei",
    ],
    storage:
      "Fresh at 0–2 °C: 5–7 days. Frozen at −40 °C: up to 18 months. Handled under strict cold-chain.",
    advantages: [
      {
        title: "High protein, lean profile",
        desc: "An excellent protein source with low fat, which is what carries it across retail, foodservice and industrial buyers alike.",
      },
      {
        title: "Farmed and wild-caught options",
        desc: "Both routes are available, so the specification can follow the market rather than the other way round.",
      },
      {
        title: "Processed for shelf life",
        desc: "Rapid processing and controlled cold chain hold freshness, texture and shelf life from harvest to delivery.",
      },
      {
        title: "Formats to suit the channel",
        desc: "Whole head-on or headless, peeled and deveined, cooked, tail-on or off, IQF — specified per order.",
      },
    ],
    deliveryOptions: ["Air freight", "Sea freight", "Bulk packaging", "Customised packaging"],
    image: shrimp.url,
    gallery: shrimp.gallery,
  },
  {
    slug: "mediterranean-sea-bass",
    category: "seafood",
    title: "Mediterranean Sea Bass",
    latinName: "Dicentrarchus labrax",
    tagline:
      "One of the most in-demand fish in the Mediterranean — delicate flavor, meaty texture, high nutritional value.",
    tags: ["ASC · GlobalG.A.P farms", "Year-round", "EU & MENA export"],
    description:
      "The Mediterranean Sea Bass is one of the most in-demand fish in the Mediterranean region, known for its delicate flavor, meaty texture and high nutritional value. At VARS Aquaculture we supply only from leading, certified Mediterranean farms, ensuring year-round availability, sustainable practices and top export quality.",
    longDescription: [
      "European sea bass (Dicentrarchus labrax) is the species Turkish Mediterranean aquaculture is built on, and the Aegean coast around Milas and the Gulf of Güllük grows most of the national volume. VARS sources from open-sea cage farms there, certified to ASC and GlobalG.A.P — certificates that belong to the farm, not to VARS.",
      "It is a lean, delicately flavoured fish with firm white flesh that portions cleanly, which is why it carries both the retail counter and the restaurant plate. Fish is harvested against a confirmed order rather than drawn from stock, chilled on flake ice at 0–2 °C and flown out within days; consolidation through İzmir is what puts it in Europe, the Gulf or East Asia the day after it leaves the water.",
      "Continuous production across 52 weeks makes supply a scheduling question rather than a seasonal one, and standing weekly volumes are the normal arrangement for distributor and HORECA accounts. Every consignment carries a health certificate, a batch certificate of analysis, a certificate of origin and the commercial documents, with TRACES pre-notification for the EU and SFDA-compliant Halal documentation for the Gulf.",
    ],
    highlights: [
      "Farm certifications (ASC & GlobalG.A.P)",
      "Long-term supply chain stability via Mediterranean hub alliances",
      "52-week continuous production",
      "Optimized EMEA & North America logistics",
    ],
    metrics: [
      { label: "Energy", value: "114 kcal" },
      { label: "Protein", value: "22.25 g" },
      { label: "PUFA", value: "0.83 g" },
      { label: "MUFA", value: "1.10 g" },
    ],
    specifications: [
      { label: "Product forms", value: "Whole Round · Descaled & Gutted · Dressed · Fillets" },
      { label: "Preservation", value: "Fresh / Frozen / Canned / Smoked" },
      { label: "Storage", value: "0 to +2 °C fresh · −18 °C deep-frozen" },
      { label: "Size groups", value: "XS 100–300 g · Medium 300–600 g · Large 600 g+" },
      { label: "Salt", value: "0.12 g" },
      { label: "SFA", value: "0.54 g" },
      { label: "Packaging", value: "6 kg and 10 kg EPS (expanded polystyrene) units" },
    ],
    applications: ["Canning & smoking", "HORECA / restaurant", "Retail wholesale", "Processing"],
    storage: "Optimal preservation at 0 °C to +2 °C for fresh; −18 °C for deep-frozen fillets.",
    advantages: [
      {
        title: "Certified Mediterranean farms only",
        desc: "Supplied from leading certified farms operating sustainable practices, to export quality.",
      },
      {
        title: "Muscle density from high-flow water",
        desc: "Grown in high-flow environments, which builds the dense muscle structure that holds a clean slice.",
      },
      {
        title: "Adapts across the whole channel",
        desc: "From quick-service and premium casual through to fine dining, the same fish works at every level of preparation.",
      },
    ],
    image: seaBassImg.url,
    culinaryInfo: {
      whyChooseTitle: "Why Choose VARS Sea Bass?",
      whyChoosePoints: [
        {
          title: "Essential Fatty Acids",
          desc: "High Omega-3 content and essential micronutrients maintained through specialized bio-diets.",
        },
        {
          title: "Architectural Texture",
          desc: "Muscle density achieved through high-flow water environments, perfect for precision slicing.",
        },
        {
          title: "Market Versatility",
          desc: "From quick-service premium casual to Michelin-star fine dining, our Sea Bass adapts to any culinary vision.",
        },
      ],
      cookingSuggestions: [
        "Oven-baking whole with herbs and olive oil",
        "Crispy skin pan-frying",
        "Charcoal grilling",
        "Mediterranean salt-crust roasting",
      ],
      flavorProfile:
        "Mild, sweet flavor with a firm, flaky texture that holds its shape during high-heat preparation.",
      sizeGroups: [
        { group: "XS-Small", weight: "100–300 g", application: "Canning & Portioning" },
        {
          group: "Medium (HoReCa)",
          weight: "300–600 g",
          application: "Restaurant Standard Portion",
        },
        { group: "Large Selection", weight: "600–800 g", application: "Fine Dining Whole Fish" },
        { group: "Executive Cut", weight: "800–1500 g", application: "Premium Fillet & Steak" },
      ],
      nutritionalNumbers: [
        { label: "Energy", value: "114 kcal" },
        { label: "Protein", value: "22.25 g" },
        { label: "Saturated Fats", value: "0.54 g" },
        { label: "Monounsaturated Fats", value: "1.10 g" },
        { label: "Polyunsaturated Fats", value: "0.83 g" },
        { label: "Salt", value: "0.12 g" },
      ],
    },
    seoKeywords: [
      "Mediterranean Sea Bass",
      "Dicentrarchus labrax",
      "Branzino wholesale",
      "loup de mer export",
      "fresh sea bass export",
      "D&G sea bass",
      "air freight sea bass",
      "European sea bass",
      "Akdeniz levrek",
      "levrek ihracat",
      "taze levrek toptan",
    ],
    searchSynonyms: [
      "seabass",
      "sea bass",
      "branzino",
      "loup de mer",
      "levrek",
      "akdeniz levrek",
      "levrek ihracatı",
      "fresh seabass",
      "D&G bass",
    ],
  },
  {
    slug: "mediterranean-sea-bream",
    category: "seafood",
    title: "Mediterranean Sea Bream",
    latinName: "Sparus aurata",
    tagline:
      "Gilthead sea bream — delicate flavor, firm flesh and exceptional nutritional profile from certified Mediterranean farms.",
    tags: ["ASC · GlobalG.A.P farms", "Whole / D&G / Fillet", "EU export"],
    description:
      "The Mediterranean Sea Bream (Sparus aurata), also known as gilthead sea bream, is one of the Mediterranean's most prized fish, valued for its delicate flavor, firm flesh and exceptional nutritional profile. At VARS Aquaculture we source from leading certified farms in the Mediterranean region, ensuring sustainable production, year-round availability and the highest export quality.",
    longDescription: [
      "Gilthead sea bream (Sparus aurata) is, with sea bass, the backbone of Mediterranean aquaculture, and Türkiye is one of its largest producers. VARS sources from open-sea cage farms holding ASC and GlobalG.A.P certification, where the water exchange that keeps flesh firm is a function of the site rather than of the feeding regime.",
      "It is supplied whole, gutted, dressed or filleted, at 134 kcal and 20.4 g of protein per 100 g, with a fat profile — 2.50 g MUFA against 1.85 g PUFA — that holds up to smoking and canning as well as to the grill. Fish is harvested against a confirmed order rather than drawn from stock, chilled on flake ice at 0–2 °C and flown out within days.",
      "Production runs across 52 weeks, so supply is a scheduling question rather than a seasonal one, and standing weekly or monthly volumes are the normal arrangement for distributor and HORECA accounts. Certification belongs to the producing farm and is issued in its name; VARS is a trading company and holds none of its own.",
    ],
    highlights: [
      "Mediterranean farms certified to ASC and GlobalG.A.P",
      "Delicate flavor and firm white flesh",
      "Available whole, gutted, dressed, or filleted",
      "52-week continuous production and global delivery",
    ],
    metrics: [
      { label: "Energy", value: "134 kcal" },
      { label: "Protein", value: "20.44 g" },
      { label: "PUFA", value: "1.85 g" },
      { label: "MUFA", value: "2.50 g" },
    ],
    specifications: [
      { label: "Product forms", value: "Whole Round · Descaled & Gutted · Dressed · Fillets" },
      { label: "Preservation", value: "Fresh / Frozen / Canned / Smoked" },
      { label: "Storage", value: "0 to +2 °C fresh · −18 °C deep-frozen" },
      { label: "Size groups", value: "XS 100–300 g · Medium 300–600 g · Large 600 g+" },
      { label: "Salt", value: "0.12 g" },
      { label: "SFA", value: "1.13 g" },
      { label: "Packaging", value: "6 kg and 10 kg EPS (expanded polystyrene) units" },
    ],
    applications: ["Canning & smoking", "HORECA / restaurant", "Retail wholesale"],
    storage:
      "Optimal preservation at 0 °C to +2 °C for fresh variants; −18 °C for deep-frozen fillets.",
    advantages: [
      {
        title: "Gilthead bream from certified farms",
        desc: "Sourced from certified farms in the Mediterranean region for consistent quality, year-round availability and export quality.",
      },
      {
        title: "Firm flesh, delicate flavour",
        desc: "High-quality protein and essential amino acids in a fish whose texture suits home cooking, restaurants and catering equally.",
      },
      {
        title: "Muscle density from high-flow water",
        desc: "Grown in high-flow environments, which is what produces the firmness buyers specify it for.",
      },
    ],
    image: seaBream.url,
    pdfUrl: odooDirectUrl("/odoo/documents/7xL4XfbBRhmb9LH2LJF86Qo287"),
    pdfLabel: "VARS Seafood Catalogue",
    culinaryInfo: {
      whyChooseTitle: "Why Choose VARS Sea Bream?",
      whyChoosePoints: [
        {
          title: "Rich Lipid Balance",
          desc: "Naturally balanced fat content yielding succulent, juicy white flesh upon grilling.",
        },
        {
          title: "Farming Integrity",
          desc: "Cultured in open sea cages with pristine Mediterranean water exchange.",
        },
        {
          title: "Culinary Agility",
          desc: "Exceptional response to Mediterranean citrus, olive oil, and rosemary marinades.",
        },
      ],
      cookingSuggestions: [
        "Whole fish charcoal grilling",
        "Pan-seared fillets with lemon and garlic",
        "Oven baking with roasted vine tomatoes",
        "Mediterranean seafood bouillabaisse",
      ],
      flavorProfile:
        "Moist, tender white meat with a succulent, slightly sweet flavor profile prized in Mediterranean gastronomy.",
      sizeGroups: [
        { group: "XS-Small", weight: "100–300 g", application: "Smoking & Processing" },
        {
          group: "Medium (HoReCa)",
          weight: "300–600 g",
          application: "Hotel & Restaurant Entrees",
        },
        {
          group: "Large Selection",
          weight: "600–800 g",
          application: "Sharing Platter Whole Roast",
        },
        { group: "Executive Cut", weight: "800–1200 g", application: "Chef Special Whole Fish" },
      ],
      nutritionalNumbers: [
        { label: "Energy", value: "134 kcal" },
        { label: "Protein", value: "20.44 g" },
        { label: "Saturated Fats", value: "1.13 g" },
        { label: "Monounsaturated Fats", value: "2.50 g" },
        { label: "Polyunsaturated Fats", value: "1.85 g" },
        { label: "Salt", value: "0.12 g" },
      ],
    },
    seoKeywords: [
      "Mediterranean Sea Bream",
      "Sparus aurata",
      "Dorade Royale wholesale",
      "gilt head sea bream",
      "fresh sea bream export",
      "D&G sea bream",
      "European sea bream",
      "Akdeniz çipura",
      "çipura ihracat",
      "taze çipura toptan",
    ],
    searchSynonyms: [
      "seabream",
      "sea bream",
      "dorade",
      "gilt-head bream",
      "çipura",
      "akdeniz çipura",
      "çipura ihracatı",
      "fresh seabream",
      "dorade royale",
    ],
  },
  {
    slug: "amberjack",
    category: "seafood",
    title: "Greater Amberjack",
    latinName: "Seriola dumerili",
    tagline:
      "Turkey-origin Greater Amberjack — firm texture, export-grade quality for wholesale, HORECA and premium distribution.",
    tags: ["Akya Fish", "Mediterranean Amberjack", "EU · Middle East"],
    description:
      "Greater Amberjack is widely valued in wholesale, foodservice and premium distribution channels for its firm texture and versatility. Also traded as Amberjack, Mediterranean Amberjack, and Akya Fish. Export-grade quality aligned with EU and Middle East standards.",
    longDescription: [
      "Greater amberjack (Seriola dumerili) is the Mediterranean's answer to the Japanese yellowtail trade — a firm, high-fat pelagic that holds a clean slice and does not fall apart under heat. Turkish production is comparatively young, which is why most buyers meet it first as a sashimi species and only later as a grill fish.",
      "At 145 kcal and 20.5 g of protein per 100 g, with 2.7 g PUFA, it carries more fat than bass or bream, and that is the point: the fat is what makes it work raw. It ships whole on flake ice at 0–2 °C under an unbroken cold chain, because for a raw-market species the chain is the product specification, not a precaution.",
      "Formats and grading are agreed per order against the harvest size available that week. Consignments carry the standard export set — health certificate, certificate of analysis, certificate of origin, invoice and packing list — with TRACES pre-notification for the EU and SFDA-compliant Halal documentation for Gulf destinations.",
    ],
    highlights: [
      "Turkey-origin Greater Amberjack (Seriola dumerili)",
      "Export-grade quality aligned with EU and Middle East standards",
      "Suitable for wholesale, HORECA and distribution markets",
    ],
    metrics: [
      { label: "Energy", value: "145 kcal" },
      { label: "Protein", value: "20.5 g" },
      { label: "PUFA", value: "2.7 g" },
      { label: "MUFA", value: "1.7 g" },
    ],
    specifications: [
      { label: "Standards", value: "Farms certified to ASC / GlobalG.A.P" },
      { label: "Availability", value: "52-week continuous production" },
      { label: "SFA", value: "1.3 g" },
      { label: "Salt", value: "0.1 g" },
      { label: "Logistics", value: "EMEA & North America fresh delivery" },
      { label: "Packaging", value: "6 kg and 10 kg EPS (expanded polystyrene) units" },
    ],
    applications: ["Sashimi and premium grill", "HORECA / restaurant", "Wholesale & distribution"],
    storage: "Hold at 0 – 2 °C on flake ice; strict cold-chain from harvest to delivery.",
    advantages: [
      {
        title: "Rich in healthy fats and Omega-3",
        desc: "The fat content is what supports the taste and texture the premium market pays for.",
      },
      {
        title: "Dense muscle from strong currents",
        desc: "Naturally dense muscle structure built in strong water currents — it slices clean and holds together under heat.",
      },
      {
        title: "Strong demand across three regions",
        desc: "Highly valued in HORECA, sushi and fine dining, with established demand across the EU, the Middle East and Asia.",
      },
      {
        title: "Export grade for EU and Middle East",
        desc: "Specified to the standards those two markets apply.",
      },
    ],
    // Recovered from the retired product page. Unlike the size table on the
    // same pages — which is identical boilerplate across all six species —
    // this copy is genuinely specific to amberjack: the Akya şiş reference is
    // Turkish-market, and the water-current point is about this fish.
    culinaryInfo: {
      whyChooseTitle: "Why VARS Greater Amberjack?",
      whyChoosePoints: [
        {
          title: "Firm & Structured Texture",
          desc: "Naturally dense muscle structure from strong open-water currents, which is what lets it hold a clean slice raw and keep its shape under heat.",
        },
        {
          title: "Rich in Essential Fatty Acids",
          desc: "2.7 g PUFA and 1.7 g MUFA per 100 g — more fat than sea bass or bream, and the reason it works as a raw-market species.",
        },
        {
          title: "Market Versatility",
          desc: "Valued across HORECA, sushi and fine dining, with demand in the EU, the Middle East and East Asia.",
        },
      ],
      flavorProfile:
        "Rich, slightly oily meat with a clean ocean taste and a firm, dense texture — suited to both grilling and sashimi.",
      cookingSuggestions: [
        "Grilled steaks with olive oil and sea salt",
        "Charcoal-grilled, Akya şiş style (Turkish market)",
        "Sashimi or carpaccio from premium cuts",
        "Oven-roasted with Mediterranean herbs",
      ],
    },
    image: amberjack.url,
    seoKeywords: [
      "Mediterranean Amberjack",
      "Seriola dumerili",
      "yellowtail amberjack",
      "sashimi grade amberjack",
      "fresh amberjack wholesale",
      "Akya fish export",
      "sarıkuyruk ihracat",
    ],
    searchSynonyms: [
      "amberjack",
      "yellowtail",
      "seriola",
      "sashimi amberjack",
      "akya",
      "amberjack export",
    ],
  },
  {
    slug: "rainbow-trout",
    category: "seafood",
    title: "Rainbow Trout",
    latinName: "Oncorhynchus mykiss",
    tagline:
      "Mild flavor, tender texture, high nutritional value — farmed rainbow trout for retail, foodservice and premium distribution.",
    tags: ["EU export standards", "Year-round", "Wholesale · HORECA · Retail"],
    description:
      "Rainbow Trout is globally valued for its mild flavor, tender texture and high nutritional value, making it ideal for retail, foodservice and premium distribution channels. Sourced from certified aquaculture farms with export-grade quality aligned with EU standards.",
    longDescription: [
      "Rainbow trout (Oncorhynchus mykiss) is the one species in this range that is not a marine cage fish, and it behaves differently in trade because of it: freshwater production is steadier week to week, so trout is the line buyers use to hold a programme together when marine supply tightens.",
      "It runs at 130 kcal and 21 g of protein per 100 g with 2.0 g MUFA — mild enough for retail and fatty enough for the smoke house, which is where a large share of European volume ends up. Fish is held at 0–2 °C on flake ice from harvest to delivery, and the cold chain is logged end to end rather than asserted.",
      "Availability is year-round from certified farms, which makes trout straightforward to put on a standing weekly or monthly schedule. The export document set is the same as for the marine species, and a continuous programme turns that documentation into a routine rather than a per-shipment exercise.",
    ],
    highlights: [
      "Farmed Rainbow Trout (Oncorhynchus mykiss)",
      "Sourced from certified aquaculture farms",
      "Export-grade quality aligned with EU standards",
      "Year-round availability",
      "Suitable for wholesale, HORECA and retail markets",
    ],
    metrics: [
      { label: "Energy", value: "130 kcal" },
      { label: "Protein", value: "21 g" },
      { label: "PUFA", value: "1.6 g" },
      { label: "MUFA", value: "2.0 g" },
    ],
    specifications: [
      { label: "Product forms", value: "Whole Round · D&G · Fillets" },
      { label: "Standards", value: "Farms certified to ASC / GlobalG.A.P" },
      { label: "SFA", value: "1.1 g" },
      { label: "Salt", value: "0.1 g" },
      { label: "Logistics", value: "Fresh delivery to EMEA & North America" },
      { label: "Packaging", value: "6 kg and 10 kg EPS (expanded polystyrene) units" },
    ],
    applications: ["Smoke house", "HORECA / restaurant", "Retail wholesale"],
    storage: "Hold at 0 – 2 °C on flake ice; maintain cold-chain from harvest to delivery.",
    advantages: [
      {
        title: "Certified aquaculture farms",
        desc: "Sourced from certified farms, to export-grade quality aligned with EU standards.",
      },
      {
        title: "Rich in Omega-3 and protein",
        desc: "Naturally high in Omega-3 and high-quality protein.",
      },
      {
        title: "Soft, flaky, mild",
        desc: "A flesh and flavour profile that takes every cooking method, which is why it moves through retail and foodservice alike.",
      },
      {
        title: "Suits retail through to processing",
        desc: "Retail, restaurants, catering and processed seafood applications all take the same product.",
      },
    ],
    deliveryOptions: ["Air freight", "Sea freight", "Bulk packaging", "Customised packaging"],
    // Recovered from the retired product page; the size table on that page is
    // shared boilerplate and is deliberately not carried over.
    culinaryInfo: {
      whyChooseTitle: "Why VARS Rainbow Trout?",
      whyChoosePoints: [
        {
          title: "High Nutritional Value",
          desc: "21 g of protein per 100 g at 130 kcal, with 2.0 g MUFA — lean enough for retail, rich enough for the smoke house.",
        },
        {
          title: "Tender & Delicate Texture",
          desc: "Soft, flaky flesh that portions cleanly and takes on cure and smoke without drying out.",
        },
        {
          title: "Versatile Market Use",
          desc: "Moves into retail, foodservice and smoking alike, and freshwater production keeps supply steadier week to week than marine species.",
        },
      ],
      flavorProfile:
        "Mild and delicate with a soft, flaky texture and a light, clean finish — suitable for every common cooking method.",
      cookingSuggestions: [
        "Oven-baked with herbs and lemon",
        "Grilled with light seasoning",
        "Pan-fried with butter and garlic",
        "Cold- or hot-smoked for deeper flavour",
      ],
    },
    image: rainbowTrout.url,
    seoKeywords: [
      "Rainbow Trout wholesale",
      "Oncorhynchus mykiss",
      "farmed rainbow trout export",
      "fresh trout fillets",
      "smoked trout wholesale",
      "alabalık ihracat",
    ],
    searchSynonyms: ["rainbow trout", "trout", "alabalık", "fresh trout", "trout fillets"],
  },
  {
    slug: "olive-flounder",
    category: "seafood",
    title: "Olive Flounder",
    latinName: "Paralichthys olivaceus",
    tagline:
      "One of the world's most valuable marine species — the top farmed finfish in South Korea for over two decades.",
    tags: ["Sushi / Sashimi grade", "ASC · GlobalG.A.P farms", "Fine dining"],
    description:
      "The Olive Flounder (Paralichthys olivaceus) has been the top farmed finfish in South Korea for over two decades. Known for its delicate flavor, tender texture and high nutritional value, it is a premium choice for sushi, sashimi and high-end culinary applications. At VARS Aquaculture we source olive flounders from trusted, certified farms, ensuring sustainability, year-round availability and export-grade quality.",
    longDescription: [
      "Olive flounder (Paralichthys olivaceus) has been the leading farmed finfish in South Korea for more than twenty years, and the Korean raw market is what shaped the product: graded by live weight, handled for texture rather than yield, and judged on how it slices. VARS supplies it from certified partner farms in Korea.",
      "It is the one species in this catalogue offered in all three forms — frozen fillet, fresh gutted and live — with grading bands that follow harvest size rather than a packing convention. Two fillets come from one fish, so piece weight tracks the live grade: the 2.0–2.5 kg fish, the largest we produce, gives the 500–600 g/pc band.",
      "At 91 kcal and 18.8 g of protein per 100 g it is a lean fish, held at 0–2 °C on flake ice with the cold chain unbroken from harvest. Harvest cycles run across 52 weeks. Tell us the destination airport and we will confirm whether the corridor fits the window before you commit to an order.",
    ],
    highlights: [
      "Top farmed finfish in South Korea for over two decades",
      "Sushi & sashimi grade delicate flavor and firm texture",
      "Farmed by ASC & GlobalG.A.P certified producers",
      "52-week harvest cycles with fresh EMEA & NA delivery",
    ],
    metrics: [
      { label: "Energy", value: "91 kcal" },
      { label: "Protein", value: "18.8 g" },
      { label: "PUFA", value: "0.26 g" },
      { label: "MUFA", value: "0.65 g" },
    ],
    specifications: [
      { label: "Standards", value: "Farms certified to ASC / GlobalG.A.P" },
      // Origin moved out of the product title and into the specs: buyers search
      // "olive flounder", not "Korean olive flounder", but the sourcing origin
      // is still a fact they need before they quote.
      { label: "Origin", value: "South Korea — certified partner farms" },
      { label: "Availability", value: "Year-round, 52-week harvest cycles" },
      { label: "SFA", value: "0.28 g" },
      { label: "Salt", value: "0.23 g" },
      { label: "Logistics", value: "EMEA & North America fresh delivery" },
      { label: "Packaging", value: "6 kg and 10 kg EPS (expanded polystyrene) units" },
    ],
    exportForms: [
      {
        key: "frozen-fillet",
        name: "Frozen fillet",
        summary:
          "Skin-on fillets frozen to export grade, cut two per fish and graded by piece weight.",
        sizes: ["400 – 500 g/pc", "500 – 600 g/pc"],
        options: [
          {
            label: "Fins",
            value:
              "Attached or trimmed, to order. Korean buyers typically ask for fins on; it is your choice.",
            choices: ["Attached", "Trimmed"],
          },
          {
            label: "Seasoning",
            value: "Plain, or lightly salted on request.",
            choices: ["Plain", "Lightly salted"],
          },
        ],
        notes:
          "Two fillets come from one fish, so piece weight follows harvest size: our largest grade, a 2.0 – 2.5 kg live fish, yields the 500 – 600 g/pc band.",
      },
      {
        key: "fresh-gutted",
        name: "Fresh gutted",
        summary:
          "Whole gutted fish on flake ice, air-freighted for raw-market and sashimi counters.",
        sizes: [
          "400 – 600 g/pc",
          "600 – 800 g/pc",
          "800 – 1000 g/pc",
          "1000 – 1200 g/pc",
          "1200 – 1500 g/pc",
          "1500 – 2000 g/pc",
          "2000 – 2500 g/pc",
        ],
        notes: "2.0 – 2.5 kg is the largest live fish we produce, which sets the top band.",
      },
      {
        key: "live",
        name: "Live",
        summary:
          "Packed in oxygen-filled bags or dedicated air containers for live-tank restaurants and retail.",
        options: [
          {
            label: "Viability window",
            value:
              "24 – 30 hours door-to-door under optimal conditions. Beyond 30 hours the risk rises sharply and we will not ship it.",
          },
          {
            label: "Worked example — Korea to the Gulf",
            value:
              "3 – 5 h packing and transfer to ICN, 9 – 10 h flight, 3 – 6 h arrival handling, customs and pickup. Roughly 18 – 24 h total, inside the safe window.",
          },
        ],
        notes:
          "Tell us the destination airport and we will confirm whether the corridor fits the window before you commit to an order.",
      },
    ],
    // TRACES is the EU's notification system, not a document — listing it as
    // one is the category error docs/project/evidence/credentials-and-claims.md §2 warns about,
    // and it signals to an EU importer that the supplier does not know what
    // TRACES is. The farm certificates stay: they are real, they travel with
    // the consignment, and they belong to the producing facility rather than
    // to VARS, which holds no certification of its own.
    exportDocuments: [
      "Health / veterinary certificate",
      "Certificate of Analysis (CoA)",
      "Certificate of Origin",
      "Commercial invoice & packing list",
      "Farm GlobalG.A.P certificate",
      "Farm ASC certificate",
      "TRACES pre-notification (EU consignments)",
    ],
    applications: ["Sushi & sashimi", "Fine dining HORECA", "Premium retail"],
    storage: "Hold at 0 – 2 °C on flake ice; strict cold-chain from harvest to delivery.",
    advantages: [
      {
        title: "South Korea's leading farmed finfish",
        desc: "Top farmed finfish in South Korea for over two decades — the reference market for the species.",
      },
      {
        title: "Premium raw-market quality",
        desc: "Delicate flavour and tender texture make it a sushi, sashimi and high-end kitchen fish rather than a commodity flatfish.",
      },
      {
        title: "Certified farms, year-round",
        desc: "Sourced from trusted certified farms for sustainability, year-round availability and export-grade quality.",
      },
    ],
    image: oliveFlounder.url,
    pdfUrl: odooDirectUrl("/odoo/documents/7xL4XfbBRhmb9LH2LJF86Qo287"),
    pdfLabel: "VARS Seafood Catalogue",
    seoKeywords: [
      "Korean Olive Flounder",
      "Paralichthys olivaceus",
      "Hirame wholesale",
      "flatfish export",
      "sashimi grade flounder",
      "Japon pisi balığı",
    ],
    searchSynonyms: [
      "olive flounder",
      "hirame",
      "flounder",
      "flatfish",
      "korean flounder",
      "halibut del Japón",
      "japanische flunder",
    ],
  },
  {
    slug: "meagre",
    category: "seafood",
    title: "Meagre",
    latinName: "Argyrosomus regius",
    tagline:
      "Mild sweet flavor, tender white flesh — high-protein, low-fat Mediterranean species from certified farms.",
    tags: ["ASC · GlobalG.A.P farms", "High protein · low fat", "Fine dining"],
    description:
      "Meagre (Argyrosomus regius) is a prized Mediterranean species known for its mild, sweet flavor and firm white flesh. Popular with chefs and seafood buyers alike, it offers a high-protein, low-fat profile. At VARS Aquaculture we source Meagre from certified Mediterranean farms, ensuring year-round availability, sustainable practices and export-grade freshness.",
    longDescription: [
      "Meagre (Argyrosomus regius) is the large, lean Mediterranean species that fine dining reaches for when bass and bream are too familiar: mild, faintly sweet, and firm enough to portion into thick cuts that hold their shape. In Turkish it is sarıağız, and it is farmed in Aegean cages alongside the volume species.",
      "The nutritional case is unusual for a fish this size — 93 kcal and 18 g of protein per 100 g against just 0.64 g of saturated fat — which is why it appears on menus written around lean protein. It is stored on flake ice at 0–2 °C and, like the rest of the fresh range, harvested to order rather than held.",
      "Production is continuous across 52 weeks, so a restaurant group can plan around it. Note that meagre is not brown meagre (Sciaena umbra), a smaller and separate species this catalogue previously confused it with; the certificates and the Latin name on the paperwork identify Argyrosomus regius.",
    ],
    highlights: [
      "Prized Mediterranean species with mild, sweet flavor",
      "High-protein, low-fat nutritional profile",
      "Sourced from certified Mediterranean farms",
      "52-week continuous production for fine dining & wholesale",
    ],
    metrics: [
      { label: "Energy", value: "93 kcal" },
      { label: "Protein", value: "18 g" },
      { label: "SFA", value: "0.64 g" },
      { label: "PUFA", value: "0.68 g" },
    ],
    seoKeywords: [
      "VARS Meagre",
      "Argyrosomus regius",
      "Corvina wholesale",
      "stone bass export",
      "fresh Mediterranean meagre",
      "gutted meagre",
      "sarıağız ihracat",
    ],
    searchSynonyms: [
      "brown meagre",
      "meagre",
      "corvina",
      "stone bass",
      "meagre fish",
      "fresh meagre export",
      "granyöz",
      "kaya levreği",
      "sarıağız",
    ],
    specifications: [
      { label: "Standards", value: "Farms certified to ASC / GlobalG.A.P" },
      { label: "Availability", value: "52-week continuous production" },
      { label: "MUFA", value: "0.72 g" },
      { label: "Salt", value: "0.06 g" },
      { label: "Logistics", value: "Regular fresh deliveries to EMEA & North America" },
      { label: "Packaging", value: "6 kg and 10 kg EPS (expanded polystyrene) units" },
    ],
    applications: ["Fine dining HORECA", "Premium retail", "Export wholesale"],
    storage: "Store on flake ice at 0 – 2 °C.",
    advantages: [
      {
        title: "Firm white flesh that holds its form",
        desc: "Large flakes and low fat make it one of the few species that survives whole-fish grilling and salt-crust roasting intact.",
      },
      {
        title: "A specialty position, not a commodity",
        desc: "Still comparatively rare on export menus, which is what gives it its place on a fine-dining list.",
      },
      {
        title: "Graded to the plate",
        desc: "Size grading is specified per order so the fish matches the preparation it is bought for.",
      },
    ],
    image: meagre.url,
    culinaryInfo: {
      whyChooseTitle: "Why Choose VARS Meagre?",
      whyChoosePoints: [
        {
          title: "Essential Fatty Acids",
          desc: "High Omega-3 content and essential micronutrients maintained through specialized bio-diets.",
        },
        {
          title: "Architectural Texture",
          desc: "Muscle density achieved through high-flow water environments, perfect for precision slicing.",
        },
        {
          title: "Market Versatility",
          desc: "From quick-service premium casual to Michelin-star fine dining, our fish adapts to any culinary vision.",
        },
      ],
      cookingSuggestions: [
        "Grilled whole with herbs and lemon",
        "Oven-baked with Mediterranean vegetables",
        "Pan-fried fillets with olive oil",
        "Steamed for a delicate texture",
      ],
      flavorProfile:
        "This fish offers lean, flavorful meat with a mild taste, making it ideal for a variety of recipes. It is easy to prepare and pairs perfectly with Mediterranean flavors.",
      sizeGroups: [
        { group: "XS-Small", weight: "100–200g / 200–300g", application: "Canning & Smoking" },
        {
          group: "Medium (HoReCa)",
          weight: "300–400g / 400–600g",
          application: "Restaurant Standard",
        },
        {
          group: "Large Selection",
          weight: "600–800g / 800–1000g",
          application: "Fine Dining Whole Roast",
        },
        { group: "Executive Cut", weight: "1000–1500g", application: "Premium Fillet Steak" },
      ],
      nutritionalNumbers: [
        { label: "Energy", value: "93 kcal" },
        { label: "Protein", value: "18 g" },
        { label: "Saturated Fats", value: "0.64 g" },
        { label: "Polyunsaturated Fats", value: "0.68 g" },
        { label: "Monounsaturated Fats", value: "0.72 g" },
        { label: "Salt", value: "0.06 g" },
      ],
    },
  },
  {
    slug: "blue-crab",
    category: "seafood",
    title: "Blue Crab",
    tagline:
      "Sweet, delicate meat with rich flavor — live, fresh-frozen and boiled-frozen blue crab from sustainable fisheries.",
    tags: ["Wild-caught", "Live / Frozen / Boiled", "HORECA · Processing"],
    description:
      "Blue Crab is a premium seafood product, valued for its sweet, delicate meat and rich flavor, widely used in global cuisines and high-end foodservice markets. At VARS Aquaculture we supply live, fresh-frozen and boiled-frozen blue crabs, handled with strict quality control to preserve freshness, texture and taste.",
    longDescription: [
      "Blue crab is a wild-caught fishery product rather than a farmed one, and that shapes everything about how it trades: volumes follow the catch, grading follows what comes aboard, and the premium sits with the sweet white body meat rather than with size alone.",
      "VARS supplies it live, fresh-frozen and boiled-frozen. Live product is held chilled at 0–4 °C, frozen at −18 °C, and the format decides the route — live and chilled move by air because hours matter, frozen by reefer container because it does not. At 90 kcal and 19 g of protein per 100 g it is lean, and it is bought for flavour rather than for yield.",
      "The main destinations are foodservice and processing, where boiled-frozen product goes straight into picking lines. Every consignment carries its health certificate, certificate of analysis and certificate of origin, and traces back to the catch and the processing facility — which is what makes the health certificate mean anything.",
    ],
    highlights: [
      "Wild-caught from sustainable fisheries",
      "Sweet, high-quality white meat",
      "Available in live and frozen formats",
      "Export-grade quality with international standards",
      "Suitable for wholesale, HORECA and processing markets",
    ],
    metrics: [
      { label: "Energy", value: "90 kcal" },
      { label: "Protein", value: "19 g" },
      { label: "PUFA", value: "0.4 g" },
      { label: "MUFA", value: "0.3 g" },
    ],
    specifications: [
      { label: "Standards", value: "Farms certified to ASC / GlobalG.A.P" },
      { label: "Formats", value: "Live · Fresh-frozen · Boiled-frozen" },
      { label: "SFA", value: "0.3 g" },
      { label: "Salt", value: "0.25 g" },
      { label: "Logistics", value: "Fresh delivery to EMEA & North America" },
      { label: "Packaging", value: "6 kg and 10 kg EPS (expanded polystyrene) units" },
    ],
    applications: ["HORECA / restaurant", "Processing", "Wholesale distribution"],
    seoKeywords: [
      "blue crab wholesale",
      "live blue crab export",
      "boiled frozen blue crab",
      "Aegean blue crab",
      "crab meat supplier",
      "mavi yengeç ihracat",
    ],
    searchSynonyms: ["blue crab", "mavi yengeç", "crab", "live crab", "boiled crab", "crab meat"],
    storage: "Live product chilled at 0–4 °C; frozen products at −18 °C.",
    advantages: [
      {
        title: "Wild-caught from sustainable fisheries",
        desc: "Harvested rather than farmed, from fisheries managed for continuity of supply.",
      },
      {
        title: "Sweet, tender white meat",
        desc: "Naturally sweet flavour with a tender, juicy texture — the reason it holds its place in premium seafood dishes.",
      },
      {
        title: "Live and frozen formats",
        desc: "Live, fresh-frozen and boiled-frozen, handled under strict quality control to preserve freshness, texture and taste.",
      },
      {
        title: "Takes every cooking method",
        desc: "Steaming, boiling, frying and prepared dishes all work from the same supply.",
      },
    ],
    deliveryOptions: [
      "Air freight for live and urgent orders",
      "Sea freight for frozen bulk shipments",
      "Custom logistics per destination",
    ],
    image: seaBream.url,
  },
  {
    slug: "oyster",
    category: "seafood",
    title: "Oyster",
    tagline:
      "Briny-sweet flavor, smooth texture — premium oysters from clean, nutrient-rich waters and certified farms.",
    tags: ["Depurated", "Live / Processed", "Fine dining"],
    description:
      "Oysters are a premium seafood delicacy, valued for their briny-sweet flavor, smooth texture and high nutritional value — widely used in fine dining and raw seafood applications. At VARS Aquaculture we source oysters from clean, nutrient-rich waters and certified farms, ensuring consistent quality, sustainability and export-ready standards.",
    longDescription: [
      "Oysters are sold on where they were grown and how they were finished, not on a species name. VARS sources from certified farms in clean, nutrient-rich waters and supplies depurated product — purged in clean water before dispatch, which is the step that makes raw consumption safe and is not optional for an export shipment.",
      "They are supplied live or processed. Live oysters hold 10–14 days at 0–2 °C, which is the whole logistics window and the reason they fly rather than sail; frozen product keeps up to twelve months at −40 °C. At 65 kcal and 6.6 g of protein per 100 g they are the lightest item in this range.",
      "Fine dining and raw bars are the main outlets, where consistency of size and fill matters more than headline volume. Consignments carry the health certificate, certificate of analysis and certificate of origin, with TRACES pre-notification on EU-bound shipments; batches trace back to the growing area and the depuration facility.",
    ],
    highlights: [
      "Farmed oysters from certified, clean waters",
      "Depurated (purified) for safe consumption",
      "Premium live and processed formats available",
      "Export-grade quality with international certifications",
      "Ideal for wholesale, HORECA and premium markets",
    ],
    metrics: [
      { label: "Energy", value: "65 kcal" },
      { label: "Protein", value: "6.6 g" },
      { label: "PUFA", value: "0.3 g" },
      { label: "MUFA", value: "0.2 g" },
    ],
    specifications: [
      { label: "Standards", value: "Farms certified to ASC / GlobalG.A.P" },
      {
        label: "Formats",
        value: "Live (in shell) · Shucked · Frozen · Fresh/frozen/canned/smoked",
      },
      {
        label: "Size grades",
        value: "Small 12–18/kg · Medium 8–12/kg · Large 5–8/kg · Premium 3–5/kg",
      },
      { label: "Handling", value: "Depurated under strict hygiene controls" },
      { label: "SFA", value: "0.2 g" },
      { label: "Salt", value: "0.3 g" },
      { label: "Packaging", value: "6 kg and 10 kg EPS (expanded polystyrene) units" },
    ],
    applications: ["Fine dining", "Premium retail", "HORECA / raw bar"],
    seoKeywords: [
      "oyster wholesale",
      "depurated oyster export",
      "live oyster supplier",
      "Crassostrea gigas oyster",
      "fine dining oyster supply",
      "istiridye ihracat",
    ],
    searchSynonyms: [
      "oyster",
      "istiridye",
      "gigas oyster",
      "live oyster",
      "depurated oyster",
      "pacific oyster",
      "ostra rizada",
      "pazifische felsenauster",
    ],
    storage: "Fresh at 0–2 °C: 10–14 days. Frozen at −40 °C: up to 12 months.",
    advantages: [
      {
        title: "Depurated for safe consumption",
        desc: "Purified under strict hygiene control before dispatch — the step that makes raw service possible.",
      },
      {
        title: "Certified, clean-water farms",
        desc: "Sourced from certified farms in clean, nutrient-rich waters.",
      },
      {
        title: "Briny-sweet and creamy",
        desc: "A balanced briny-sweet taste with a smooth, creamy texture, valued in fine dining, seafood bars and HORECA.",
      },
      {
        title: "Live and processed formats",
        desc: "Premium live oysters and frozen whole or meat formats, packed to the channel.",
      },
    ],
    deliveryOptions: ["Air freight", "Sea freight", "Bulk packaging", "Customised packaging"],
    image: oyster.url,
  },
];

export function getCategory(slug: string) {
  return CATEGORIES.find((c) => c.slug === slug);
}
export function getProductsByCategory(slug: string) {
  return PRODUCTS.filter((p) => p.category === slug);
}
export function getProduct(category: string, slug: string) {
  return PRODUCTS.find((p) => p.category === category && p.slug === slug);
}
